import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import vaultService from '../services/vaultService';
import hotelService from '../services/hotelService';
import WizardHotelStep from './WizardHotelStep';
import accountService from '../services/accountService';
import vaultsourceService from '../services/vaultsourceService';
import vaultdownloadsService from '../services/vaultdownloadsService';
import v2VaultSourceSettingsService from '../services/v2VaultSourceSettingsService';
import v2VaultSettingsService from '../services/v2VaultSettingsService';
import v2VaultOhipTestService from '../services/v2VaultOhipTestService';
import v2VaultProcessService from '../services/v2VaultProcessService';
import vaultsourcegroupService from '../services/vaultsourcegroupService';
import vaultsqlService from '../services/vaultsqlService';
import vaultsqlparametersService from '../services/vaultsqlparametersService';
import vaultsqlsearchService from '../services/vaultsqlsearchService';
import vaultsendemailService from '../services/vaultsendemailService';
import vaultfoliourlService from '../services/vaultfoliourlService';
import vaultFolioService, { renderFolioPdf } from '../services/vaultFolioService';
import canonicaltemplateService from '../services/canonicaltemplateService';
import hoteltemplateService from '../services/hoteltemplateService';
import vaultPaymentService from '../services/vaultPaymentService';
import vaultReconcileService from '../services/vaultReconcileService';
import revShareService from '../services/revShareService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import VaultPaymentModal from './VaultPaymentModal';
import CredentialsPicker from './shared/CredentialsPicker';
import CredentialChip from './shared/CredentialChip';
import CredentialSelector from './shared/CredentialSelector';
import CredentialOverrides from './shared/CredentialOverrides';
import {
  renderTemplate as renderCanonicalTemplate,
  amountFormatOf,
  makeResolveVar,
  processEach as processEachBlocks,
  processConditionals as processConditionalBlocks,
  openPrintWindow as openDocumentPrintWindow,
} from '../utils/templateRender';
import { useAccount, usePartnerAccess } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import './VaultForm.css';
import './VaultQuery.css';

// Locate the vault source-setting row that backs a marketplace override field. Matches by
// setting_key case-insensitively and treats HOTELID / HOTEL_ID as the same field.
const HOTEL_ID_ALIASES = ['HOTELID', 'HOTEL_ID'];
function findVaultSettingRow(vaultSourceSettings, name) {
  const upper = String(name || '').toUpperCase();
  const aliases = HOTEL_ID_ALIASES.includes(upper) ? HOTEL_ID_ALIASES : [upper];
  return vaultSourceSettings.find(s => aliases.includes(String(s.setting_key || '').toUpperCase()));
}
// Build the { [SETTING_NAME]: value } map CredentialOverrides reads from, keyed by source setting_key.
// Hotel-id values are exposed under both aliases so the map resolves whichever name the marketplace uses.
function vaultOverrideValues(vaultSourceSettings, vaultSettingValues) {
  const out = {};
  vaultSourceSettings.forEach(s => {
    const val = vaultSettingValues[s.id] || '';
    out[s.setting_key] = val;
    if (HOTEL_ID_ALIASES.includes(String(s.setting_key || '').toUpperCase())) {
      HOTEL_ID_ALIASES.forEach(a => { out[a] = val; });
    }
  });
  return out;
}

// The picker's per-property values (CredentialSelector overridesInPicker) written back into this vault's source
// settings — the rows they live in.
function applyVaultOverrides(vaultSourceSettings, values, setVaultSettingValues) {
  const next = {};
  Object.entries(values || {}).forEach(([name, val]) => {
    const row = findVaultSettingRow(vaultSourceSettings, name);
    if (row) next[row.id] = val;
  });
  if (Object.keys(next).length) setVaultSettingValues(prev => ({ ...prev, ...next }));
}


const PLATFORMS = {
  'windows-x86_64': 'Windows (x86_64)',
  'linux-x86_64': 'Linux (x86_64)',
  'linux-arm64': 'Linux (ARM64)',
  'darwin-amd64': 'macOS (Intel)',
  'darwin-arm64': 'macOS (Apple Silicon)'
};

const PLATFORM_INSTRUCTIONS = {
  'windows-x86_64': {
    icon: '🪟',
    steps: [
      'Download the app using the Download button above.',
      'Open Command Prompt or PowerShell and navigate to your downloads folder.',
      (filename) => <span>Run the app: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>{filename}</code></span>,
      <span>If Windows SmartScreen blocks it, click <strong>More info</strong> then <strong>Run anyway</strong>.</span>
    ]
  },
  'linux-x86_64': {
    icon: '🐧',
    steps: [
      'Download the app using the Download button above.',
      'Open a terminal and navigate to your downloads folder.',
      (filename) => <span>Make it executable: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>chmod +x {filename}</code></span>,
      (filename) => <span>Run the app: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>./{filename}</code></span>
    ]
  },
  'linux-arm64': {
    icon: '🐧',
    steps: [
      'Download the app using the Download button above.',
      'Open a terminal and navigate to your downloads folder.',
      (filename) => <span>Make it executable: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>chmod +x {filename}</code></span>,
      (filename) => <span>Run the app: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>./{filename}</code></span>
    ]
  },
  'darwin-amd64': {
    icon: '🍎',
    steps: [
      'Download the app using the Download button above.',
      'Open Terminal and navigate to your downloads folder.',
      (filename) => <span>Make it executable: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>chmod +x {filename}</code></span>,
      (filename) => <span>Run the app: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>./{filename}</code></span>,
      <span>If macOS blocks the app, go to <strong>System Settings → Privacy &amp; Security</strong> and click <strong>Allow Anyway</strong>, then run again.</span>
    ]
  },
  'darwin-arm64': {
    icon: '🍎',
    steps: [
      'Download the app using the Download button above.',
      'Open Terminal and navigate to your downloads folder.',
      (filename) => <span>Make it executable: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>chmod +x {filename}</code></span>,
      (filename) => <span>Run the app: <code style={{ background: '#e8edf8', border: '1px solid #c7d4f7', borderRadius: 4, padding: '2px 6px', fontFamily: 'monospace', fontSize: 13, color: '#3a4a8a' }}>./{filename}</code></span>,
      <span>If macOS blocks the app, go to <strong>System Settings → Privacy &amp; Security</strong> and click <strong>Allow Anyway</strong>, then run again.</span>
    ]
  }
};

// minimum_retention_date may be stored as a year (e.g. 2028) or as a count of years (e.g. 3).
// Values > 100 are treated as a year and converted to a duration relative to now.
const formatPeriodEnd = (ts) => {
  const ms = Number(ts) * 1000;
  if (!ms || isNaN(ms)) return null;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const parseRetentionYears = (value) => {
  const n = parseInt(value);
  if (!n || isNaN(n)) return 1;
  if (n > 100) return Math.max(1, n - new Date().getFullYear());
  return n;
};

// Which of a search result's columns the results table shows. A vault query is `select *`, so the raw
// column list also carries machine fields (payload, s3_key) that are no use in a table — the readable
// subset is picked here instead.
//
// The leading id column is the CONFIRMATION NUMBER for both vault types. That is the number on the
// guest's paperwork and the one staff are given when someone asks for a folio; the reservation id is
// internal to the PMS and means nothing to the property.
//
// This used to depend on the vault type, because an OperaV5 folio row had no confirmation number to
// show — materialised_view_folios now carries one (from RESERVATION_NAME.CONFIRMATION_NO), so the
// distinction is gone. Still falls back to whichever of the two a result actually has, so an older
// vault, or a query selecting only some columns, still shows an id rather than a blank first column.
//
// One definition, used by both the header and the body — they were duplicated verbatim, so a change to
// one silently produced a table whose headings did not match its cells.
export const pickResultDisplayColumns = (cols) => {
  const find = (re) => (cols || []).find(c => re.test(c));
  const guestNameCol = find(/guestname/i);
  if (!guestNameCol) return (cols || []).filter(col => /name|date/i.test(col));
  const confirmationCol = find(/^confirmation(number|no)$/i);
  const reservationIdCol = find(/^reservationid$/i);
  const idCol = confirmationCol || reservationIdCol;
  return [
    idCol,
    guestNameCol,
    find(/arrival.*date|arrivaldate/i),
    find(/departure.*date|departuredate/i),
    find(/foliowindow/i),
  ].filter(Boolean);
};

// THE DETAIL POPUP'S FIELD NAMES. It listed the row's raw columns, and `reservationid` (Opera's internal
// RESV_NAME_ID, e.g. 324048) read as the confirmation number beside the real `confirmationnumber` (256322) —
// reported as a wrong confirmation number (HIL-7). Known columns get a label and come first, in this order;
// the internal id is kept but says what it is. Anything else is shown as it comes.
const FOLIO_FIELD_LABELS = [
  ['confirmationnumber', 'Confirmation No.'],
  ['foliono', 'Folio No.'],
  ['foliowindowno', 'Folio Window'],
  ['invoiceno', 'Bill No.'],
  ['resort', 'Hotel'],
  ['guestname', 'Guest'],
  ['arrivaldate', 'Arrival'],
  ['departuredate', 'Departure'],
  ['reservationid', 'Reservation ID (internal)'],
];
const FOLIO_FIELD_ORDER = new Map(FOLIO_FIELD_LABELS.map(([k], i) => [k, i]));
const FOLIO_FIELD_LABEL = new Map(FOLIO_FIELD_LABELS);
// WHICH NUMBER A FOLIO CARRIES (OperaV5): the folio number when the bill was paid by cash or card, the
// invoice number only when it was a direct bill to City Ledger — never the bill number, which is Opera's
// internal one. `numbers` is the built folio's own answer ({ printFolioNo, printInvoiceNo }, vaultfolio
// settlementOf); until it lands the row's folio number shows, and Bill No. is never shown. An OHIP row is
// left as it is: its invoiceno is the PMS's own.
const folioDetailFields = (row, { operaV5 = false, numbers = null } = {}) => {
  const fields = Object.entries(row || {})
    .filter(([k]) => !['s3_key', 'payload', 'year', 'month', 'arrival_year'].includes(k.toLowerCase()))
    .filter(([k]) => !(operaV5 && k.toLowerCase() === 'invoiceno'))
    .filter(([k]) => !(operaV5 && numbers && k.toLowerCase() === 'foliono' && !numbers.printFolioNo))
    .sort(([a], [b]) => (FOLIO_FIELD_ORDER.get(a.toLowerCase()) ?? 999) - (FOLIO_FIELD_ORDER.get(b.toLowerCase()) ?? 999))
    .map(([k, v]) => [k, FOLIO_FIELD_LABEL.get(k.toLowerCase()) || k, v]);
  if (operaV5 && numbers && numbers.printInvoiceNo) {
    const at = fields.findIndex(([k]) => k.toLowerCase() === 'foliowindowno') + 1;
    fields.splice(at, 0, ['invoice_no', 'Invoice No.', numbers.printInvoiceNo]);
  }
  return fields;
};

// The numbers the built folio says to show, from its first (the clicked) window.
const folioNumbersOf = (payload) => {
  const parsed = typeof payload === 'string' ? (() => { try { return JSON.parse(payload); } catch (e) { return null; } })() : payload;
  const w = parsed?.reservationFolioInformation?.folioWindows?.[0];
  return w ? { printFolioNo: w.printFolioNo ?? null, printInvoiceNo: w.printInvoiceNo ?? null } : null;
};

function VaultForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id, hotelid } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedAccount, selectedPartner, selectedHotel, accountData, isChainAdmin } = useAccount();
  const effectiveHotelId = hotelid || searchParams.get('hotelid') || selectedHotel;
  const { isSuperAdmin, isPartner, user } = useAuth();
  const { stripeReady, hasVaultProducts, partnerData } = usePartnerAccess(isSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hotelName, setHotelName] = useState('');
  const [hotelRecord, setHotelRecord] = useState({});
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountPartnerId, setAccountPartnerId] = useState(null);
  const [vaultsources, setVaultSources] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('windows-x86_64');
  const [generating, setGenerating] = useState(false);
  const [generateSuccess, setGenerateSuccess] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [generatingDownloadId, setGeneratingDownloadId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('details'); // kept for compatibility
  const [formData, setFormData] = useState({
    hotelid: '',
    vaultsourceid: '',
    vaultid: '',
    name: '',
    description: '',
    status: 'initiated', // Default status
    active: 'Y', // Default active
    database_type: '',
    host: '',
    port: '',
    user: '',
    password: '',
    databasename: '',
    minimum_retention_date: '3',
    extraction_start_date: '',
    extraction_end_date: '',
    invoice_enabled: 0,
    invoice_target: '',
    use_hconnect_credentials: 0,
    credential_entity_type: null,
    credential_entity_id: null,
    credential_id: null,
  });
  const [credentialSources, setCredentialSources] = useState([]);
  const [showAddCredForm, setShowAddCredForm]     = useState(false);
  const [addCredEntityType, setAddCredEntityType] = useState(null);
  const [inlineCredName, setInlineCredName]       = useState('');
  const [inlineCredFields, setInlineCredFields]   = useState({});
  const [marketplaceSettingsDefs, setMarketplaceSettingsDefs] = useState([]);
  const [savingCreds, setSavingCreds]             = useState(false);
  const [credSaveError, setCredSaveError]         = useState(null);
  const OHIP_MARKETPLACE_ID = 37;

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const selectedVaultSource = vaultsources.find(vs => vs.id === parseInt(formData.vaultsourceid)) || null;
  const isApiExtract = selectedVaultSource?.vault_type === 'api_extract';

  // S3 upload location for superadmin — derived from local data, no lambda call needed.
  // Prefix format matches generate.js: {partnerid}/{accountid}/{hotelid}/{vault.id}
  const derivedS3Location = (() => {
    const bucket = process.env.REACT_APP_VAULT_UPLOAD_BUCKET;
    if (!bucket) return null;
    const prefix = formData.s3prefix || (() => {
      const pid = accountPartnerId || partnerData?.partnerid;
      const aid = accountId;
      const hid = formData.hotelid;
      const vid = formData.id;
      return (pid && aid && hid && vid) ? `${pid}/${aid}/${hid}/${vid}` : null;
    })();
    return { bucket, prefix };
  })();
  const [vaultSourceSettings, setVaultSourceSettings] = useState([]);
  const [vaultSettingValues, setVaultSettingValues] = useState({}); // { [v2_vault_source_setting_id]: string }
  const [editingCred, setEditingCred] = useState(false); // when true, show the picker instead of the selected-credential chip
  const [ohipTestResult, setOhipTestResult] = useState(null); // null | { loading } | { success, message, persisted? }
  const [settingsChangedSinceTest, setSettingsChangedSinceTest] = useState(false);
  const [processResult, setProcessResult] = useState(null); // null | { loading } | { success, message }
  const [processing, setProcessing] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const lastPolledStatus = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState(null); // null = not loaded, [] = loaded empty (files in current folder)
  const [fileFolders, setFileFolders] = useState([]);       // sub-folders in the current folder
  const [filesPrefix, setFilesPrefix] = useState('');        // current folder path, relative to the vault root
  const [filesTruncated, setFilesTruncated] = useState(false);
  const [filesNextToken, setFilesNextToken] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState(null);   // set when the /files call fails
  const [filesMessage, setFilesMessage] = useState(null); // informational note the lambda returns when empty
  const [uploadLocation, setUploadLocation] = useState(null); // { bucket, prefix } — superadmin only
  const [estimation, setEstimation] = useState(null); // null | { loading } | { reservationCount, totalApiCalls, estimatedCost, costPer10k, groupEstimates }
  const [estimating, setEstimating] = useState(false);
  // OHIP extract verification modal: null | { reprocessType: null|'full'|'skipExtraction'|'pdfsOnly', step: 'warning'|'code_sent', codeValue, sending, confirming, error, sentEmail }
  const [ohipExtractModal, setOhipExtractModal] = useState(null);

  // Search tab state
  const [sourceGroups, setSourceGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupSqls, setGroupSqls] = useState([]);
  const [groupSqlsLoading, setGroupSqlsLoading] = useState(false);
  const [expandedSqlId, setExpandedSqlId] = useState(null);
  const [sourceGroupsLoaded, setSourceGroupsLoaded] = useState(false);
  const [paramValues, setParamValues] = useState({}); // { [sqlId]: { [paramName]: value } }
  const [searchResult, setSearchResult] = useState(null); // { sqlName, sqlObj, loading, error, data: { columns, rows } }
  const [templateModal, setTemplateModal] = useState(null); // { step, name, html, row }
  const [templateFieldsOpen, setTemplateFieldsOpen] = useState(false);
  const [payloadModal, setPayloadModal] = useState(null); // { json: object, loading?, error? }
  // Canonical folio payloads already built this session, keyed `<vaultId>:<reservationId>` — see
  // getCanonicalFolio. Building one is a run of Athena queries; the detail modal and the Payload button
  // both want the same object.
  const canonicalFolioCache = useRef(new Map());
  const [pdfModal, setPdfModal] = useState(null); // null | { loading } | { url } | { error }
  // The PDF render is a round trip to Chromium — a second or two cold. Its own state rather than a flag
  // on pdfModal, so a failed render leaves the modal and its already-rendered preview intact instead of
  // replacing them with an error.
  const [folioPdfBusy, setFolioPdfBusy] = useState(false);
  const [folioPdfError, setFolioPdfError] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { to, subject, html, sending, error, sent }
  const [paymentModal, setPaymentModal] = useState(null); // null | { pendingFormData }
  const [confirmModal, setConfirmModal] = useState(null); // null | { message, onConfirm }
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [invoiceResending, setInvoiceResending] = useState(false);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [summaryLoadKey, setSummaryLoadKey] = useState(0);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [revShareSummary, setRevShareSummary] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [availableGroups, setAvailableGroups] = useState([]); // all groups for the vault source
  const [selectedGroupIds, setSelectedGroupIds] = useState([]); // chosen group ids
  const [settingsOpen, setSettingsOpen] = useState(false); // settings panel open on vault ready page

  const GROUP_STEP     = 2;
  const SETTINGS_STEP  = 3;
  const PAYMENT_STEP   = 4;
  const DOWNLOAD_STEP  = 5; // DB-extract (non-API) vaults only
  // OperaV5 (non-API) collapses Extraction / Records Processed / Vault System Tables Created into a
  // single "Extract & Process" step, so those three constants all resolve to the same step index.
  const EXTRACT_STEP   = isApiExtract ? 5 : 6;
  const PROCESSED_STEP = isApiExtract ? 6 : EXTRACT_STEP; // collapsed into Extract & Process for OperaV5
  const TABLES_STEP    = isApiExtract ? 7 : EXTRACT_STEP; // collapsed into Extract & Process for OperaV5
  const READY_STEP     = isApiExtract ? 8 : 7;

  const wizardSteps = [
    { label: 'Vault Details' },
    { label: 'Data Groups' },
    { label: isApiExtract ? 'API Settings' : 'Database Connection' },
    { label: 'Payment' },
    ...(!isApiExtract ? [{ label: 'Download App' }] : []),
    ...(isApiExtract
      ? [{ label: 'Extraction' }, { label: 'Records Processed' }, { label: 'Vault System Tables Created' }]
      : [{ label: 'Extract & Process' }]),
    { label: 'Vault Ready' },
  ];

  const STATUS_TO_STEP = {
    'initiated': GROUP_STEP,
    'payment': PAYMENT_STEP,
    'connection_successful': EXTRACT_STEP,
    'records_extracted': EXTRACT_STEP,
    'client_generated': isApiExtract ? EXTRACT_STEP : DOWNLOAD_STEP,
    'client_executed': EXTRACT_STEP,
    'records_processed': PROCESSED_STEP,
    'vault_tables_created': TABLES_STEP,
    'vault_ready': READY_STEP,
    // A failed extraction lands on the Extraction step so the error banner and the
    // reprocess action are shown, rather than collapsing back to Download App.
    'error': EXTRACT_STEP,
  };

  // 'initiated' is overloaded — it means both "brand-new vault" and "payment completed,
  // waiting for the extraction step function to update the status". Disambiguate using the
  // payment-indicator columns so a paid vault never lands back on the Data Groups step.
  const resolveWizardStep = (vaultData) => {
    const status = vaultData?.status;
    const paid = !!(vaultData?.stripe_subscription_id || vaultData?.use_free_seat || vaultData?.invoice_enabled);
    if (status === 'initiated' && paid) return isApiExtract ? EXTRACT_STEP : DOWNLOAD_STEP;
    const fromStatus = STATUS_TO_STEP[status] || 1;
    const fromDb = vaultData?.wizard_step || 1;
    return Math.max(fromStatus, fromDb);
  };

  useEffect(() => {
    // Auto-populate hotelid if creating from hotel page or returning from hotel creation
    if (effectiveHotelId && isCreateMode) {
      console.log('Setting hotelid:', effectiveHotelId);
      setFormData(prev => ({ ...prev, hotelid: parseInt(effectiveHotelId) }));
      fetchHotel(effectiveHotelId);
    }
  }, [effectiveHotelId, isCreateMode]);

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchVault();
    }
  }, [id, isEditMode, isViewMode]);

  useEffect(() => {
    if (isCreateMode && formData.hotelid) {
      vaultService.getCredentialSources(formData.hotelid)
        .then(data => setCredentialSources(data?.sources || []))
        .catch(() => setCredentialSources([]));
    }
  }, [isCreateMode, formData.hotelid]);

  // Refresh available credentials every time the user lands on the API Settings step,
  // so newly-shared chain/partner credentials show up without a page reload.
  useEffect(() => {
    if (wizardStep === SETTINGS_STEP && isApiExtract && formData.hotelid) {
      reloadCredentialSources();
    }
  }, [wizardStep, isApiExtract, formData.hotelid]);

  const reloadCredentialSources = async () => {
    if (!formData.hotelid) return [];
    try {
      const data = await vaultService.getCredentialSources(formData.hotelid);
      const sources = data?.sources || [];
      setCredentialSources(sources);
      return sources;
    } catch {
      return [];
    }
  };

  // Entity levels this user can save OHIP credentials to. Mirrors the Payment Porter wizard logic.
  const credEntityOptions = () => {
    const opts = [];
    const pid = selectedPartner || partnerData?.partnerid || accountData?.account?.partnerid;
    if ((isSuperAdmin || isPartner) && pid)        opts.push({ type: 'partner', id: pid,             label: 'Partner' });
    if ((isSuperAdmin || isChainAdmin || isPartner) && selectedAccount) opts.push({ type: 'account', id: selectedAccount, label: 'Chain' });
    if (formData.hotelid)                          opts.push({ type: 'hotel',   id: formData.hotelid, label: 'Hotel' });
    return opts;
  };

  const openAddCredForm = async () => {
    setCredSaveError(null);
    setInlineCredName('');
    const opts = credEntityOptions();
    setAddCredEntityType(opts[0]?.type || 'hotel');
    if (marketplaceSettingsDefs.length === 0) {
      try {
        const res  = await marketplacesettingsService.getSchema({ MARKETPLACEID: OHIP_MARKETPLACE_ID });
        const defs = (res.settings || []).filter(s => (s.required || '').toLowerCase() === 'y');
        setMarketplaceSettingsDefs(defs);
        const fields = {};
        defs.forEach(d => { fields[d.SETTINGNAME] = ''; });
        setInlineCredFields(fields);
      } catch {
        setMarketplaceSettingsDefs([]);
      }
    } else {
      const fields = {};
      marketplaceSettingsDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
      setInlineCredFields(fields);
    }
    setShowAddCredForm(true);
  };

  const handleSaveInlineCredentials = async () => {
    if (!inlineCredName.trim()) { setCredSaveError('Credential name is required'); return; }
    const opts   = credEntityOptions();
    const target = opts.find(o => o.type === addCredEntityType) || opts[0];
    if (!target) { setCredSaveError('No entity available to save credentials to'); return; }

    const isAboveHotel = target.type === 'account' || target.type === 'partner';
    const missing = marketplaceSettingsDefs.filter(d => {
      if (isAboveHotel && d.SETTINGNAME === 'HOTELID') return false;
      return !inlineCredFields[d.SETTINGNAME]?.trim();
    });
    if (missing.length > 0) { setCredSaveError('All required fields must be filled in'); return; }

    setSavingCreds(true);
    setCredSaveError(null);
    try {
      const settings = marketplaceSettingsDefs.map(d => ({
        name:  d.SETTINGNAME,
        value: (isAboveHotel && d.SETTINGNAME === 'HOTELID') ? 'N/A' : (inlineCredFields[d.SETTINGNAME] || ''),
      }));
      const saveRes = await entityMktSettingsService.saveCredentials(target.type, target.id, OHIP_MARKETPLACE_ID, inlineCredName.trim(), settings);
      const savedCredentialId = saveRes?.credentialId;

      // Chain/partner-level credentials are gated by entity_credential_shares — make sure this
      // hotel is granted access so the credential it was just saved against is actually usable.
      if (isAboveHotel && formData.hotelid && savedCredentialId) {
        try {
          const sharesRes  = await entityMktSettingsService.getShares(target.type, target.id, OHIP_MARKETPLACE_ID, savedCredentialId);
          const existing   = sharesRes?.shares || [];
          const hotelChain = accountId || null;
          const hotelIdNum   = Number(formData.hotelid);
          const hotelChainNum = hotelChain != null ? Number(hotelChain) : null;
          const alreadyCovered = existing.some(s =>
            (s.shared_with_hotel_id   != null && Number(s.shared_with_hotel_id)   === hotelIdNum) ||
            (hotelChainNum != null && s.shared_with_account_id != null && Number(s.shared_with_account_id) === hotelChainNum)
          );
          if (!alreadyCovered) {
            const merged = [
              ...existing.map(s => s.shared_with_hotel_id
                ? { targetEntityType: 'hotel',   targetEntityId: s.shared_with_hotel_id }
                : { targetEntityType: 'account', targetEntityId: s.shared_with_account_id }
              ),
              { targetEntityType: 'hotel', targetEntityId: formData.hotelid },
            ];
            await entityMktSettingsService.saveShares(target.type, target.id, OHIP_MARKETPLACE_ID, savedCredentialId, merged);
          }
        } catch (err) {
          console.warn('Failed to share credentials with this hotel', err);
        }
      }

      await reloadCredentialSources();
      if (savedCredentialId) {
        setFormData(prev => ({ ...prev, credential_entity_type: target.type, credential_entity_id: target.id, credential_id: savedCredentialId, use_hconnect_credentials: 0 }));
      }
      setShowAddCredForm(false);
      const fields = {};
      marketplaceSettingsDefs.forEach(d => { fields[d.SETTINGNAME] = ''; });
      setInlineCredFields(fields);
      setOhipTestResult(null);
      setSettingsChangedSinceTest(true);
    } catch (err) {
      setCredSaveError(err.response?.data?.message || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  // Fetch downloads after vault data is loaded (when we have the numeric vault.id)
  useEffect(() => {
    if ((isEditMode || isViewMode) && formData.id) {
      fetchDownloads();
    }
  }, [formData.id, isEditMode, isViewMode]);

  useEffect(() => {
    // Fetch hotel when hotelid is known — needed in all modes to populate accountId for payment
    if (formData.hotelid) {
      fetchHotel(formData.hotelid);
    }
  }, [formData.hotelid]);

  useEffect(() => {
    // Fetch all vault sources for the dropdown
    fetchVaultSources();
    // Create-mode hotel selection is handled by the shared WizardHotelStep (self-fetching, partner-scoped).
  }, []);

  // Fetch vault source settings (key/type definitions) and vault-specific values
  useEffect(() => {
    if (selectedVaultSource?.id && isApiExtract) {
      v2VaultSourceSettingsService.getSettingsBySourceId(selectedVaultSource.id)
        .then(r => setVaultSourceSettings(r.settings || []))
        .catch(() => setVaultSourceSettings([]));

      if ((isEditMode || isViewMode) && id) {
        v2VaultSettingsService.getSettingsByVaultId(id)
          .then(r => {
            const values = {};
            (r.settings || []).forEach(s => {
              values[s.v2_vault_source_setting_id] = s.setting_value || '';
            });
            setVaultSettingValues(values);
          })
          .catch(() => setVaultSettingValues({}));
      }
    } else {
      setVaultSourceSettings([]);
      setVaultSettingValues({});
    }
  }, [selectedVaultSource?.id, isApiExtract]);

  // Auto-open payment modal when arriving at payment step and payment is still pending
  useEffect(() => {
    if (wizardStep !== PAYMENT_STEP) return;
    const paymentDone = (STATUS_TO_STEP[formData.status] || 1) >= PAYMENT_STEP && formData.status !== 'initiated';
    if (!paymentDone && formData.hotelid && !paymentModal) {
      const effectiveAccountId = accountId || hotelRecord?.accountid || selectedAccount;
      if (effectiveAccountId) {
        setPendingSubmitData({ formData, isApiExtract, effectiveAccountId });
        setPaymentModal({ hotelId: formData.hotelid, accountId: effectiveAccountId });
      }
    }
  }, [wizardStep, partnerData, formData.status, formData.hotelid, accountId]);

  // Load invoice details when vault is awaiting invoice payment
  useEffect(() => {
    const invoicePending = formData.status === 'payment' && parseInt(formData.invoice_enabled) === 1;
    if (!invoicePending || !id || invoiceDetails) return;
    vaultPaymentService.getInvoice(id)
      .then(res => setInvoiceDetails(res.data.invoice))
      .catch(err => console.error('Failed to load invoice details:', err.message));
  }, [formData.status, formData.invoice_enabled, id]); // eslint-disable-line

  // Load payment summary for any saved vault — regardless of status — so payment info
  // is always visible even if the vault status update failed after payment was taken.
  useEffect(() => {
    if (!id) return;
    vaultPaymentService.getSummary(id)
      .then(res => setPaymentSummary(res.data))
      .catch(err => console.error('Failed to load payment summary:', err.message));
  }, [id, summaryLoadKey]); // eslint-disable-line

  // Poll payment summary every 10s when on the payment step and there are pending invoices
  useEffect(() => {
    if (!id || wizardStep !== PAYMENT_STEP) return;
    const hasPendingInvoice = paymentSummary?.invoices?.some(i => i.status === 'pending');
    if (!hasPendingInvoice) return;
    const interval = setInterval(() => {
      vaultPaymentService.getSummary(id)
        .then(res => setPaymentSummary(res.data))
        .catch(err => console.error('Payment summary poll failed:', err.message));
    }, 10000);
    return () => clearInterval(interval);
  }, [id, wizardStep, paymentSummary]); // eslint-disable-line

  // Update current time every second for real-time countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, []);

  // Poll vault status while extraction is running — triggered by pollingActive flag
  // or automatically when on an in-progress extraction step
  useEffect(() => {
    const onExtractionStep = (!isApiExtract && wizardStep === DOWNLOAD_STEP) || wizardStep === EXTRACT_STEP || wizardStep === PROCESSED_STEP || wizardStep === TABLES_STEP;
    if ((!pollingActive && !onExtractionStep) || !id) return;
    const interval = setInterval(fetchVaultStatus, 5000);
    return () => clearInterval(interval);
  }, [pollingActive, wizardStep, id]); // eslint-disable-line

  // Auto-load the S3 extraction contents when landing on the extraction step —
  // for both OperaV5 (binary upload) and OHIP (api_extract) vaults.
  useEffect(() => {
    if (wizardStep === EXTRACT_STEP && id && uploadedFiles === null) {
      loadUploadedFiles();
    }
  }, [wizardStep, id]); // eslint-disable-line

  // For OHIP vaults, load upload location so superadmin can see the expected S3 path
  useEffect(() => {
    if (isApiExtract && wizardStep === EXTRACT_STEP && id && isSuperAdmin && !uploadLocation) {
      v2VaultProcessService.listUploadedFiles(id)
        .then(result => { if (result.bucket) setUploadLocation({ bucket: result.bucket, prefix: result.prefix || null }); })
        .catch(() => {});
    }
  }, [wizardStep, id, isSuperAdmin]); // eslint-disable-line

  // Progress bar animation - 2 minutes to 100%
  useEffect(() => {
    if (!generatingDownloadId) {
      setProgress(0);
      return;
    }

    // Check if the download is still pending/generating
    const download = downloads.find(d => d.id === generatingDownloadId);
    if (download && download.status === 'completed') {
      setGeneratingDownloadId(null);
      setProgress(0);
      return;
    }

    // Update progress every 100ms
    // 2 minutes = 120000ms, so increment = 100 / (120000 / 100) = 0.0833% per 100ms
    const increment = 100 / 1200; // 100 / (120000ms / 100ms)
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          return 100;
        }
        return Math.min(prev + increment, 100);
      });
    }, 100);

    return () => clearInterval(timer);
  }, [generatingDownloadId, downloads]);

  // Check for any generating downloads on load
  useEffect(() => {
    if (downloads.length > 0) {
      const generatingDownload = downloads.find(d =>
        d.status === 'pending' || d.status === 'generating'
      );
      if (generatingDownload && !generatingDownloadId) {
        setGeneratingDownloadId(generatingDownload.id);
      }
    }
  }, [downloads]);

  // Poll for download status updates when there are pending/generating downloads
  useEffect(() => {
    const vaultId = parseInt(formData.id);

    // Check if there are any downloads in pending or generating state
    const hasActiveDownloads = downloads.some(d =>
      d.status === 'pending' || d.status === 'generating'
    );

    console.log('Polling effect triggered:', {
      downloadsCount: downloads.length,
      hasActiveDownloads,
      vaultId: vaultId,
      formDataId: formData.id,
      downloads: downloads.map(d => ({ id: d.id, name: d.name, status: d.status }))
    });

    if (!hasActiveDownloads || !vaultId || isNaN(vaultId)) {
      console.log('Polling not needed:', { hasActiveDownloads, vaultId });
      return; // No need to poll
    }

    console.log('🔄 Starting polling for download status updates... (every 5 seconds)');

    // Poll every 5 seconds
    const pollInterval = setInterval(() => {
      console.log('🔄 Polling for download updates...');
      fetchDownloads(vaultId);
    }, 5000);

    return () => {
      console.log('⏹️ Stopping polling');
      clearInterval(pollInterval);
    };
  }, [downloads, formData.id]);

  const fetchVaultStatus = async () => {
    try {
      const response = await vaultService.getVaultById(id, hotelid);
      const vaultData = response.vault || response;
      let summary = vaultData.records_summary;
      if (typeof summary === 'string') {
        try { summary = JSON.parse(summary); } catch (e) { summary = null; }
      }
      setFormData(prev => ({ ...prev, status: vaultData.status, records_summary: summary }));
      // Only advance the wizard step when vault status has genuinely changed —
      // prevents the poll from pushing the user back forward after they navigate back
      if (vaultData.status !== lastPolledStatus.current) {
        lastPolledStatus.current = vaultData.status;
        const polledStep = resolveWizardStep(vaultData);
        setWizardStep(prev => Math.max(prev, polledStep));
      }
      if (vaultData.status === 'vault_ready' || vaultData.status === 'error') {
        setPollingActive(false);
      }
    } catch (e) {
      // silently ignore polling errors
    }
  };

  const fetchVault = async () => {
    try {
      setLoading(true);
      const response = await vaultService.getVaultById(id, hotelid);
      const vaultData = response.vault || response;
      console.log('Vault data loaded:', { id: vaultData.id, vaultid: vaultData.vaultid });

      // Format DATE fields to YYYY-MM-DD for <input type="date">
      const toDateStr = (v) => v ? new Date(v).toISOString().slice(0, 10) : '';
      if (vaultData.extraction_start_date) vaultData.extraction_start_date = toDateStr(vaultData.extraction_start_date);
      if (vaultData.extraction_end_date)   vaultData.extraction_end_date   = toDateStr(vaultData.extraction_end_date);
      if (vaultData.minimum_retention_date) vaultData.minimum_retention_date = String(vaultData.minimum_retention_date);

      // Parse JSON columns if returned as strings
      if (typeof vaultData.records_summary === 'string') {
        try { vaultData.records_summary = JSON.parse(vaultData.records_summary); } catch (e) { vaultData.records_summary = null; }
      }
      if (typeof vaultData.night_audit_paths === 'string') {
        try { vaultData.night_audit_paths = JSON.parse(vaultData.night_audit_paths); } catch (e) { vaultData.night_audit_paths = []; }
      }
      if (!Array.isArray(vaultData.night_audit_paths)) vaultData.night_audit_paths = [];
      // Migrate legacy string entries to { path, hotel } objects
      vaultData.night_audit_paths = vaultData.night_audit_paths.map(e =>
        (typeof e === 'string') ? { path: e, hotel: '' } : e
      );

      // Legacy 'own' and HConnect-managed ('hconnect') modes are gone — clear them so the picker
      // forces the user to pick an explicitly-shared credential card.
      if (vaultData.credential_entity_type === 'own' || vaultData.credential_entity_type === 'hconnect') {
        vaultData.credential_entity_type = null;
        vaultData.credential_entity_id   = null;
      }
      vaultData.use_hconnect_credentials = 0;

      setFormData(vaultData);
      if (vaultData.credential_sources) setCredentialSources(vaultData.credential_sources);

      // Restore persisted connection test result
      if (vaultData.api_test_result) {
        setOhipTestResult({
          success: vaultData.api_test_result === 'passed',
          message: vaultData.api_test_message || (vaultData.api_test_result === 'passed' ? 'OHIP login successful' : 'Connection test failed'),
          persisted: true,
        });
        setSettingsChangedSinceTest(false);
      }

      // Seed the last-polled status so the first poll doesn't re-advance after a back navigation
      lastPolledStatus.current = vaultData.status;
      // Sync wizard step with loaded vault status — never go backwards
      setWizardStep(prev => Math.max(prev, resolveWizardStep(vaultData) || 2));

      // Auto-start polling if vault loaded mid-extraction
      const inProgressStatuses = ['connection_successful', 'records_extracted', 'records_processed', 'vault_tables_created'];
      if (inProgressStatuses.includes(vaultData.status)) {
        setPollingActive(true);
      }

      // Immediately fetch downloads after vault data is set
      if (vaultData.id) {
        console.log('Fetching downloads for vault ID:', vaultData.id);
        await fetchDownloads(vaultData.id);
      }
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 404) {
        navigate('/vaults');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch vault');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDownloads = async (vaultId = null) => {
    try {
      const numericVaultId = parseInt(vaultId || formData.id);

      if (!numericVaultId || isNaN(numericVaultId)) {
        console.log('Numeric vault ID not yet available, skipping downloads fetch');
        return;
      }

      console.log('Fetching downloads for numeric vault ID:', numericVaultId);
      const response = await vaultdownloadsService.getAllVaultDownloads({ vaultid: numericVaultId });
      console.log('Downloads response:', response);
      console.log('Downloads data:', response.data);

      const downloadsData = response.data?.vaultdownloads || [];
      console.log('Downloads count:', downloadsData.length);
      console.log('Downloads array:', downloadsData);

      setDownloads(downloadsData);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
      console.error('Error details:', err.response?.data);
      setDownloads([]);
    }
  };

  const fetchHotel = async (hotelId) => {
    try {
      const response = await hotelService.getHotelById(hotelId);
      const hotelData = response.hotel || response;
      setHotelName(hotelData.hotelname || '');
      setHotelRecord(hotelData);
      setAccountId(hotelData.accountid);
      // Fetch account data if we have accountid
      if (hotelData.accountid) {
        fetchAccount(hotelData.accountid);
      }
    } catch (err) {
      console.error('Failed to fetch hotel:', err);
    }
  };

  const fetchAccount = async (accId) => {
    try {
      const response = await accountService.getAccountById(accId);
      const accountData = response.account || response;
      setAccountName(accountData.COMPANYNAME || '');
      if (accountData.partnerId || accountData.partnerid) {
        setAccountPartnerId(accountData.partnerId || accountData.partnerid);
      }
    } catch (err) {
      console.error('Failed to fetch account:', err);
    }
  };

  const fetchVaultSources = async () => {
    try {
      const response = await vaultsourceService.getAllVaultSources();
      setVaultSources(response.vaultsources || []);
    } catch (err) {
      console.error('Failed to fetch vault sources:', err);
    }
  };

  // Fetch reconciliation when vault is ready and on READY_STEP (OHIP vaults only)
  useEffect(() => {
    if (wizardStep === READY_STEP && id && isApiExtract && formData.status === 'vault_ready' && reconciliation === null) {
      vaultReconcileService.get(id)
        .then(res => setReconciliation(res.data?.reconciliation || null))
        .catch(err => console.error('Failed to fetch reconciliation:', err.message));
    }
  }, [wizardStep, id, isApiExtract, formData.status, reconciliation]);

  // Fetch rev share summary for superadmin/partner when vault is ready
  useEffect(() => {
    if (wizardStep === READY_STEP && id && formData.status === 'vault_ready' && (isSuperAdmin || isPartner) && revShareSummary === null) {
      revShareService.getVaultSummary(id)
        .then(res => setRevShareSummary(res.data || null))
        .catch(err => console.error('Failed to fetch rev share summary:', err.message));
    }
  }, [wizardStep, id, formData.status, isSuperAdmin, isPartner, revShareSummary]);

  // Load available groups for the chosen vault source, and existing selections for saved vaults
  useEffect(() => {
    if (!formData.vaultsourceid) return;
    vaultsourcegroupService.getAllVaultSourceGroups({ vaultsourceid: formData.vaultsourceid })
      .then(resp => {
        const groups = resp.vaultsourcegroups || [];
        setAvailableGroups(groups);
        // Default: select all if no vault saved yet
        if (!id) setSelectedGroupIds(groups.map(g => g.id));
      })
      .catch(err => console.error('Failed to fetch vault source groups:', err));
  }, [formData.vaultsourceid]); // eslint-disable-line

  // Load existing selections when vault is loaded
  useEffect(() => {
    if (!id) return;
    vaultsourcegroupService.getVaultGroupSelections(id)
      .then(resp => {
        const sel = resp.selections || [];
        setSelectedGroupIds(sel.map(s => s.vault_source_group_id));
      })
      .catch(err => console.error('Failed to fetch vault group selections:', err));
  }, [id]);

  // Fetch source groups when search tab is activated
  useEffect(() => {
    if (wizardStep === READY_STEP && !sourceGroupsLoaded && formData.vaultsourceid) {
      const fetchSourceGroups = async () => {
        try {
          const resp = await vaultsourcegroupService.getAllVaultSourceGroups({ vaultsourceid: formData.vaultsourceid });
          const groups = resp.vaultsourcegroups || [];
          setSourceGroups(groups);
        } catch (err) {
          console.error('Failed to fetch source groups:', err);
        } finally {
          setSourceGroupsLoaded(true);
        }
      };
      fetchSourceGroups();
    }
  }, [wizardStep, sourceGroupsLoaded, formData.vaultsourceid]);

  const handleGroupSelect = async (group) => {
    setSelectedGroup(group);
    setGroupSqls([]);
    setParamValues({});
    setSearchResult(null);
    setExpandedSqlId(null);
    setGroupSqlsLoading(true);
    try {
      const resp = await vaultsqlService.getAllVaultSqls({ vaultsourcegroupid: group.id });
      setGroupSqls(resp.vaultsqls || []);
    } catch (err) {
      console.error('Failed to fetch vault SQLs:', err);
      setGroupSqls([]);
    } finally {
      setGroupSqlsLoading(false);
    }
  };

  const handleSqlSelect = async (sqlId) => {
    setExpandedSqlId(sqlId);
    setSearchResult(null);
    if (!sqlId) return;

    // Use cached parameters if already fetched
    const existing = groupSqls.find(s => s.id === sqlId);
    if (!existing || existing.parameters !== undefined) return;

    try {
      const paramsResp = await vaultsqlparametersService.getAllVaultSqlParameters({ vaultsqlid: sqlId });
      const parameters = paramsResp.vaultsqlparameters || [];
      setGroupSqls(prev => prev.map(s => s.id === sqlId ? { ...s, parameters } : s));
      const initialValues = {};
      parameters.forEach(param => { initialValues[param.name] = param.value || ''; });
      setParamValues(prev => ({ ...prev, [sqlId]: initialValues }));
    } catch {
      setGroupSqls(prev => prev.map(s => s.id === sqlId ? { ...s, parameters: [] } : s));
    }
  };

  const handleParamChange = (sqlId, paramName, value) => {
    setParamValues(prev => ({
      ...prev,
      [sqlId]: { ...prev[sqlId], [paramName]: value }
    }));
  };

  const handleSqlSearch = async (sql) => {
    setSearchResult({ sqlName: sql.name, sqlObj: sql, loading: true, error: null, data: null });
    try {
      const values = paramValues[sql.id] || {};
      const result = await vaultsqlsearchService.search(id, sql.id, values);
      setSearchResult({ sqlName: sql.name, sqlObj: sql, loading: false, error: null, data: result });
    } catch (err) {
      setSearchResult({ sqlName: sql.name, sqlObj: sql, loading: false, error: err.response?.data?.error?.message || err.response?.data?.message || err.message, data: null });
    }
  };

  // Find the payload value from a row using a case-insensitive key lookup
  const getRowPayload = (row) => {
    if (!row) return null;
    const key = Object.keys(row).find(k => k.toLowerCase() === 'payload');
    return key ? row[key] : null;
  };

  // THE RENDERER IS SHARED — utils/templateRender.js. It used to live here, and the full-template editor
  // has to render exactly the same way against a canonical sample; two copies of a template language
  // drift, and the difference shows up as a folio that looked right in the editor and wrong in print.
  // These thin wrappers keep this file's call sites (which pass a row) unchanged.
  const templateContext = (row) => ({
    row,
    payload: getRowPayload(row),
    scopes: { hotel: hotelRecord, account: accountData },
  });
  const resolveVar = (key, row) => makeResolveVar(templateContext(row))(key);
  const processEach = (html, row) => processEachBlocks(html, getRowPayload(row));
  const processConditionals = (html, row) => processConditionalBlocks(html, key => resolveVar(key, row));
  // amountFormat: the full template's own money format (amountFormatOf) — only a full template has one.
  const renderTemplate = (content, row, amountFormat = null) =>
    renderCanonicalTemplate(content, { ...templateContext(row), amountFormat });


  const sanitizeHtml = (html) => {
    if (!html) return '';
    // Collapse newlines + surrounding whitespace inside attribute values (e.g. broken src URLs)
    return html.replace(/([=\s]"[^"]*)\n\s*/g, (_, p1) => p1);
  };

  const renderHotelVars = (html) => {
    if (!html) return html;
    const withConditionals = processConditionals(html, null);
    return withConditionals.replace(/\{\{hotel\.([^}]+)\}\}/g, (match, field) => {
      const hotelKey = Object.keys(hotelRecord).find(k => k.toLowerCase() === field.toLowerCase());
      return hotelKey !== undefined && hotelRecord[hotelKey] !== null && hotelRecord[hotelKey] !== ''
        ? String(hotelRecord[hotelKey]) : '';
    });
  };

  const resolveHotelTemplate = async (canonicalGroupId) => {
    const hid = formData.hotelid;
    if (!hid || !canonicalGroupId) {
      console.log('[hotelTemplate] skipped — hotelId:', hid, 'canonicalGroupId:', canonicalGroupId);
      return null;
    }
    try {
      const res = await hoteltemplateService.getAll({ hotel_id: hid });
      const all = res.data.hoteltemplates || [];
      // One template per (hotel, document type)
      const match = all.find(t => parseInt(t.canonical_group_id) === parseInt(canonicalGroupId));
      return match || null;
    } catch (err) {
      console.error('[hotelTemplate] Failed to load hotel template:', err);
      return null;
    }
  };

  const handleFolioPdfAction = async (action, s3key) => {
    setPdfModal(prev => ({ ...prev, actionLoading: action }));
    try {
      const disposition = action === 'download' ? 'attachment' : 'inline';
      // The vault is named on every call now: a bare key cannot be authorised, because nothing in it
      // says whose it is (HP-126). The server confines the key to this vault's own prefix.
      const result = await vaultfoliourlService.getUrl(id, null, s3key, disposition);
      window.open(result.url, '_blank');
    } catch (e) {
      setPdfModal(prev => ({ ...prev, actionLoading: null, error: e.response?.data?.error?.message || e.message || 'PDF not available' }));
    }
    setPdfModal(prev => prev ? { ...prev, actionLoading: null } : null);
  };

  // If the row has an s3_key, fetch URL or content from the lambda
  const enrichRowWithS3Url = async (row) => {
    const s3KeyCol = Object.keys(row).find(k => k.toLowerCase() === 's3_key');
    const s3key = s3KeyCol ? row[s3KeyCol] : null;
    if (!s3key) return row;
    try {
      const result = await vaultfoliourlService.getUrl(id, null, s3key, 'inline');
      // .regcard: lambda returns HTML content directly
      if (result.content !== undefined) {
        return { ...row, s3_content: result.content };
      }
      // Other files (pdf etc): lambda returns presigned URL
      return { ...row, s3_url: result.url };
    } catch {
      return row;
    }
  };

  // THE CANONICAL FOLIO PAYLOAD, FOR EVERY VAULT TYPE.
  //
  // An OHIP folio row already carries it: the extract stored the source folio JSON in the row's
  // `payload` column, and the folio template's nested variables — {{reservationFolioInformation.
  // folioWindows.0.folios.0.postings...}} — resolve against it. An OperaV5 row has no such column,
  // because that vault holds Opera's raw tables and nothing ever assembled a folio from them. Every
  // nested variable in the template therefore came out blank and the payload view showed a flat row of
  // view columns, which is not what the PDF is built from and not what the same screen shows in prd.
  //
  // vault-folio-build assembles the identical shape on demand from the extracted tables, keyed on the
  // row's reservation id. Fetching it here and putting it on the row as `payload` means the template,
  // the print path and the Payload button all work on one object whichever PMS the vault came from —
  // and a third PMS is a resolver in that lambda, not another branch on this screen.
  //
  // Cached per reservation for the life of the screen (canonicalFolioCache, declared with the other
  // refs): the modal renders the folio and the Payload button shows it, and building it is a run of
  // Athena queries, not a lookup.

  // How this row identifies its reservation. The FIELD is sent, not just the value: a vault holds several
  // properties whose id spaces overlap, so a bare id matched against both reservation and confirmation
  // numbers returns another hotel's folio. The resort goes too, for the same reason.
  const rowFolioKey = (row) => {
    const find = (name) => {
      const k = Object.keys(row || {}).find(c => c.toLowerCase() === name);
      const v = k ? row[k] : null;
      return v === null || v === undefined || v === '' ? null : String(v);
    };
    return {
      reservationId: find('reservationid'),
      confirmationNo: find('confirmationnumber'),
      resort: find('resort') || find('hotelid'),
      // The bill itself, so the document is the folio that was clicked and not the reservation's first
      // window (HIL-7: clicking folio 43151, window 2, printed folio 43134, window 1).
      folioNo: find('foliono'),
      folioWindowNo: find('foliowindowno'),
    };
  };
  const rowReservationId = (row) => {
    const k = rowFolioKey(row);
    return k.reservationId || k.confirmationNo;
  };

  // Rows whose canonical payload has to be built rather than read: a folio row from a vault that stores
  // no payload column. An OHIP row already has one and must not be rebuilt — its stored payload is the
  // source record, and re-deriving it would replace what the hotel actually received with our version.
  const needsCanonicalBuild = (row) => selectedGroup?.data_type_key === 'folios'
    && !getRowPayload(row)
    && !!rowReservationId(row);

  const getCanonicalFolio = async (row) => {
    const folioKey = rowFolioKey(row);
    if (!folioKey.reservationId && !folioKey.confirmationNo) return null;
    // Cached per property as well as per id — two hotels in one vault can use the same number.
    const key = `${id}:${folioKey.resort || ''}:${folioKey.reservationId || `c${folioKey.confirmationNo}`}`
      + `:${folioKey.folioNo || ''}:${folioKey.folioWindowNo || ''}`;
    if (canonicalFolioCache.current.has(key)) return canonicalFolioCache.current.get(key);
    const json = await vaultFolioService.build(id, folioKey);
    canonicalFolioCache.current.set(key, json);
    return json;
  };

  // The row exactly as the folio template sees it: S3 enrichment, plus the canonical payload for the
  // vault types that need it built. Every render path goes through this, so no path can be given a row
  // the others are not.
  const enrichRowForTemplate = async (row) => {
    const enriched = await enrichRowWithS3Url(row);
    if (!needsCanonicalBuild(row)) return enriched;
    try {
      const payload = await getCanonicalFolio(row);
      return payload ? { ...enriched, payload } : enriched;
    } catch (e) {
      // The flat columns still render the folio's header fields; losing the postings is better than
      // losing the document. The Payload button reports the failure properly.
      console.error('[folio] canonical payload build failed:', e);
      return enriched;
    }
  };

  // THE FOLIO DETAIL VIEW, FOR EVERY VAULT TYPE.
  //
  // One entry point and one exit: it takes a result row, shows that row's fields, and offers the folio
  // document if the vault has one. What differs between an OHIP vault and an OperaV5 vault — where the
  // PDF lives, and whether there is one at all — is decided by the BACKEND resolver, not here. That is
  // the whole point: adding a third PMS should not touch this screen.
  //
  // Availability is checked before the modal settles so Print and Download are only offered when they
  // will work; a row with no document still shows its fields, which is most of the value.
  const openFolioDetail = async (row) => {
    const title = selectedGroup?.name || 'Folio';
    const s3KeyCol = Object.keys(row).find(k => k.toLowerCase() === 's3_key');
    const s3key = s3KeyCol ? row[s3KeyCol] : null;
    setPdfModal({ row, s3key, title, loading: true });
    try {
      const result = await vaultfoliourlService.getDocumentUrlForRow(id, row, 'inline');
      // NO STORED PDF (OperaV5): render the folio from its template in the BACKGROUND, exactly as the
      // OHIP modal checks S3 availability in the background. The modal opens immediately with the
      // fields; Print and Download stay disabled for the second or two the render takes and enable
      // themselves when it lands. Same shape of experience, different source of the document.
      if (result?.hasDocument === false) {
        setPdfModal(prev => prev ? { ...prev, loading: false, hasDocument: false, rendering: true } : null);
        try {
          const rendered = await renderFolioFromTemplate(row);
          setPdfModal(prev => prev
            ? (rendered
                ? { ...prev, rendering: false, rendered }
                : { ...prev, rendering: false, error: 'No folio template is configured for this data group, so there is nothing to print. Add one to the canonical group.' })
            : null);
        } catch (e) {
          setPdfModal(prev => prev ? { ...prev, rendering: false, error: e.response?.data?.message || e.message || 'Could not render the folio' } : null);
        }
        return;
      }
      // hasDocument:false means this vault keeps no folio PDF (OperaV5). Not an error and not worth
      // saying out loud — the footer swaps to a button that renders the folio from its template, so the
      // user gets a PDF either way and never has to know which vault type they are looking at.
      setPdfModal(prev => prev
        ? { ...prev, loading: false, hasDocument: result?.hasDocument !== false }
        : null);
    } catch (e) {
      const msg = e.response?.data?.error?.message || e.response?.data?.message
        || 'The folio document is not available for this row.';
      setPdfModal(prev => prev ? { ...prev, loading: false, error: msg } : null);
    }
  };

  // Open a rendered folio in a print window. Shared by the template modal's own button and by the folio
  // detail modal for vaults that have no stored PDF — one implementation, so both produce an identical
  // document. "Print / Save PDF" because the browser's print dialog is where the PDF actually comes from;
  // a page cannot write one directly.
  const openPrintWindow = openDocumentPrintWindow;

  // The canonical payload for one row: exactly what the folio template is given, and therefore what the
  // PDF is built from.
  //
  // FOR A FOLIO THAT IS THE CANONICAL JSON, not the row. The question this button answers is "why does
  // the folio say that", and a folio says what it says because of `reservationFolioInformation` —
  // the reservation, the guest, the folio windows and their postings (canonical group 2). A list of
  // materialised-view columns is a different object entirely; it is where the header fields come from and
  // nothing else. OHIP shows the canonical JSON here because its extract stored it; OperaV5 now shows the
  // same thing because vault-folio-build assembles it.
  //
  // For any other data group there is no canonical document — the row IS the record — so the enriched row
  // is still the honest answer, with a string `payload` column parsed so it reads as nested JSON rather
  // than one long escaped line.
  const openCanonicalPayload = async (row) => {
    try {
      if (needsCanonicalBuild(row)) {
        setPayloadModal({ loading: true });
        const json = await getCanonicalFolio(row);
        setPayloadModal({ json });
        return;
      }
      const enriched = await enrichRowWithS3Url(row);
      const json = { ...enriched };
      for (const k of Object.keys(json)) {
        if (k.toLowerCase() === 'payload' && typeof json[k] === 'string') {
          try { json[k] = JSON.parse(json[k]); } catch (e) { /* not JSON — leave the string as it is */ }
        }
      }
      // A folio's payload IS the document; showing it alone answers the question the button asks, and
      // matches what an OperaV5 folio shows now that it has one.
      setPayloadModal({ json: (selectedGroup?.data_type_key === 'folios' && json.payload && typeof json.payload === 'object') ? json.payload : json });
    } catch (e) {
      setPayloadModal({
        json: row,
        error: e.response?.data?.error?.message || e.response?.data?.message || e.message
          || 'Could not build the canonical payload for this folio.',
      });
    }
  };

  // Save a rendered folio to disk, as a PDF.
  //
  // RENDERED SERVER-SIDE BY CHROMIUM, not by a library here. The template is per-hotel HTML with its own
  // CSS, edited on /hotel/{id}/fulltemplate/{id}; the only way to guarantee the download matches what the
  // hotel designed is to use the same engine that draws the Print preview. A client-side PDF library
  // either rasterises the page — an invoice that cannot be selected, searched or read by a screen reader
  // — or needs the document rebuilt in its own drawing API, which drifts from the template on the next
  // edit. This is an archival financial document; neither is acceptable.
  //
  // Composed here rather than server-side because the portal already holds the template, the row and the
  // payload and builds exactly what Print shows. A second renderer in the lambda would be a second thing
  // to keep in step.
  //
  // OHIP vaults are unaffected: those folios download the real PDF that OHIP's own API produced and that
  // is sitting in S3. This path exists for OperaV5, where nothing in the pipeline ever made one.
  const downloadRenderedFolio = async (rendered, row) => {
    if (!rendered) return;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${rendered.name}</title>`
      + `<style>body{font-family:sans-serif;padding:20px;}@media print{body{margin:0;}}</style></head>`
      + `<body>${rendered.header || ''}${rendered.html}${rendered.footer || ''}</body></html>`;
    const findVal = (name) => {
      const k = Object.keys(row || {}).find(c => c.toLowerCase() === name);
      return k ? row[k] : null;
    };
    // Named after the folio it is, not "download.pdf": these end up in a folder with hundreds of others.
    const ref = findVal('confirmationnumber') || findVal('reservationid') || 'folio';
    const window_ = findVal('foliowindowno');
    const filename = `folio_${ref}${window_ ? `_w${window_}` : ''}.pdf`;

    setFolioPdfError(null);
    setFolioPdfBusy(true);
    try {
      const res = await renderFolioPdf(html, filename);
      // Base64 over JSON: every other route on this API is JSON, and a binary passthrough would mean
      // configuring the gateway's binary media types for one endpoint.
      const bytes = Uint8Array.from(atob(res.base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.contentType || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.filename || filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Reported on the modal, never as an alert (STYLE.md). A failed render must not look like a
      // download the browser silently blocked.
      setFolioPdfError(err.response?.data?.message || err.message || 'Could not produce the PDF.');
    } finally {
      setFolioPdfBusy(false);
    }
  };

  // Render the canonical template for one row, WITHOUT opening the template modal.
  //
  // An OperaV5 vault has no folio PDF in S3 — nothing in that pipeline produces one — but it does have
  // the folio template, which is how a folio is printed for these vaults. This is that render, so the
  // detail modal can print a folio itself instead of telling the user to go and find the template.
  // Returns null when the group has no template configured, which is the only case where there is
  // genuinely nothing to print.
  // WHICH TEMPLATE RENDERS THIS GROUP'S DOCUMENTS — hotel first, canonical second.
  //
  // A hotel's FULL template (Hotel → Templates → Full Template, keyed by vault system group) is the
  // document that hotel issues. The canonical template is the generic one every property falls back to.
  // So the hotel's own wins whenever it exists; otherwise nothing changes.
  //
  // The header and footer are NOT part of this choice. They are the parts AROUND the document
  // (v2_hotel_templates, keyed by canonical group) and are wrapped around whichever body is used — a hotel
  // that has replaced the body still wants its own letterhead on it.
  //
  // Cached for the life of the screen: it is one lookup per group, and both the detail modal and the
  // template modal ask for it.
  const hotelFullTemplateCache = useRef(new Map());

  const resolveHotelFullTemplate = async () => {
    // OPERAV5 ONLY. An OHIP vault's document is the PDF the PMS itself produced and stored — that file is
    // what the hotel actually issued, and a template would replace a real document with a re-drawing of
    // it. A V5 vault has no such file, which is why it renders from a template at all.
    if (isApiExtract) return null;
    const hid = formData.hotelid;
    if (!hid || !selectedGroup?.id) return null;
    const key = `${hid}:${selectedGroup.id}`;
    if (hotelFullTemplateCache.current.has(key)) return hotelFullTemplateCache.current.get(key);
    let found = null;
    try {
      const res = await hoteltemplateService.getAllFull({ hotel_id: hid, vault_source_group_id: selectedGroup.id });
      // An empty template is not a template: a row created and never filled in must not blank the
      // document that the canonical one would have produced.
      found = (res.data?.hotelfulltemplates || []).find(t => t.content && t.content.trim()) || null;
    } catch (err) {
      console.error('[fullTemplate] lookup failed:', err);
    }
    hotelFullTemplateCache.current.set(key, found);
    return found;
  };

  // The canonical template for this group — the fallback, unchanged: the SQL's own template if it names
  // one, otherwise the group's, preferring this hotel's country.
  const resolveCanonicalTemplate = async () => {
    const sqlObj = searchResult?.sqlObj;
    if (sqlObj?.canonical_template_id) {
      const res = await canonicaltemplateService.getById(sqlObj.canonical_template_id);
      if (res.data?.id) return res.data;
    }
    if (!selectedGroup) return null;
    const res = await canonicaltemplateService.getAll({ vault_source_group_id: selectedGroup.id });
    const templates = res.data.canonicaltemplates || [];
    const hotelCountry = (hotelRecord?.COUNTRY || '').toUpperCase();
    const countrySpecific = hotelCountry ? templates.filter(t => (t.country || '').toUpperCase() === hotelCountry) : [];
    const defaults = templates.filter(t => !t.country);
    const candidates = countrySpecific.length > 0 ? countrySpecific : (defaults.length > 0 ? defaults : templates);
    return candidates[0] || null;
  };

  // { name, content, canonicalGroupId } for whichever template wins, so callers never repeat the order.
  // canonicalGroupId is carried because the HEADER/FOOTER are keyed by it — a hotel full template has no
  // canonical group of its own, so the group's canonical template supplies the id for that lookup.
  const resolveDocumentTemplate = async () => {
    const full = await resolveHotelFullTemplate();
    const canonical = await resolveCanonicalTemplate();
    if (full) {
      console.log(`[template] using the hotel's full template "${full.name}" for ${selectedGroup?.name}`);
      return {
        name: full.name || canonical?.name || selectedGroup?.name || 'Document',
        content: full.content,
        canonicalGroupId: canonical?.canonical_group_id || null,
        // Whether this document itemises tax. Carried with the template because it belongs to the
        // template, and read by the renderer as {{#if showLineVat}} around the VAT column.
        showLineVat: full.show_line_vat === 'Y' ? 'Y' : 'N',
        // Whether a charge line prints its posting remark — {{#if showLineRemarks}} (V621).
        showLineRemarks: full.show_line_remarks === 'Y' ? 'Y' : 'N',
        // How its amounts print (V619) — a setting of the template, like showLineVat.
        amountFormat: amountFormatOf(full),
        // A FULL TEMPLATE IS THE WHOLE PAGE — no hotel header/footer around it.
        //
        // Those are the parts that surround a CANONICAL document, which carries no letterhead of its own.
        // A full template does: this one opens with the property's logo and address, so wrapping it
        // printed "Cliveden House, GB" twice and a broken image where the header's {{hotel.logo}} resolved
        // to nothing. Whoever wrote the full page owns all of it, including its top and bottom.
        wrapWithHotelHeader: false,
      };
    }
    if (!canonical) return null;
    return {
      name: canonical.name, content: canonical.content, canonicalGroupId: canonical.canonical_group_id,
      showLineVat: 'N', showLineRemarks: 'N', wrapWithHotelHeader: true,
    };
  };

  const renderFolioFromTemplate = async (row) => {
    // enrichRowForTemplate, not enrichRowWithS3Url: a folio template's postings table reads the canonical
    // payload, which an OperaV5 row only has once it is built.
    const enrichedRow = await enrichRowForTemplate(row);
    const template = await resolveDocumentTemplate();
    if (!template) return null;
    const acctTpl = template.wrapWithHotelHeader ? await resolveHotelTemplate(template.canonicalGroupId) : null;
    return {
      name: template.name,
      numbers: folioNumbersOf(enrichedRow.payload),
      html: renderTemplate(template.content, { ...enrichedRow, showLineVat: template.showLineVat, showLineRemarks: template.showLineRemarks }, template.amountFormat),
      header: renderHotelVars(sanitizeHtml(acctTpl?.header)),
      footer: renderHotelVars(sanitizeHtml(acctTpl?.footer)),
    };
  };

  const handleRowClick = async (row) => {
    if (isApiExtract) {
      const s3KeyCol = Object.keys(row).find(k => k.toLowerCase() === 's3_key');
      const s3key = s3KeyCol ? row[s3KeyCol] : null;
      // PDF files open directly in the folio detail modal
      if (s3key && s3key.toLowerCase().endsWith('.pdf')) {
        await openFolioDetail(row);
        return;
      }
      // Other file types (e.g. .regcard) fall through to template path
    }

    // A FOLIO ROW OPENS THE FOLIO VIEW, whatever PMS it came from. OHIP has always done this; an
    // OperaV5 folio went to the raw template render instead, which is a different screen for the same
    // thing. The template is still how a V5 folio is printed — it is now reached through the same modal
    // rather than instead of it.
    //
    // Only for the folios group: Preferences and Notes, Night Audit and the rest have no folio detail
    // view and the template render IS their answer.
    if (!isApiExtract && selectedGroup?.data_type_key === 'folios') {
      await openFolioDetail(row);
      return;
    }

    if (!selectedGroup) return;
    setTemplateFieldsOpen(false);
    try {
      const enrichedRow = await enrichRowForTemplate(row);

      // THE HOTEL'S OWN FULL TEMPLATE COMES FIRST (OperaV5 only — see resolveHotelFullTemplate). It is
      // the document that hotel issues, so there is nothing to choose between: render it and stop.
      const full = await resolveHotelFullTemplate();
      if (full) {
        const canonical = await resolveCanonicalTemplate();
        // No header/footer: the full template is the whole page (see resolveDocumentTemplate).
        setTemplateModal({
          step: 'render',
          name: full.name || canonical?.name || selectedGroup.name,
          html: renderTemplate(full.content, { ...enrichedRow, showLineVat: full.show_line_vat === 'Y' ? 'Y' : 'N', showLineRemarks: full.show_line_remarks === 'Y' ? 'Y' : 'N' }, amountFormatOf(full)),
          row: enrichedRow,
          header: '',
          footer: '',
        });
        return;
      }

      const sqlObj = searchResult?.sqlObj;
      if (sqlObj?.canonical_template_id) {
        const res = await canonicaltemplateService.getById(sqlObj.canonical_template_id);
        const template = res.data;
        if (template?.id) {
          const acctTpl = await resolveHotelTemplate(template.canonical_group_id);
          setTemplateModal({ step: 'render', name: template.name, html: renderTemplate(template.content, enrichedRow), row: enrichedRow, header: renderHotelVars(sanitizeHtml(acctTpl?.header)), footer: renderHotelVars(sanitizeHtml(acctTpl?.footer)) });
          return;
        }
      }

      // Fall back to source group name-based lookup
      const res = await canonicaltemplateService.getAll({ vault_source_group_id: selectedGroup.id });
      const templates = res.data.canonicaltemplates || [];
      if (templates.length === 0) {
        setTemplateModal({ step: 'empty' });
        return;
      }

      // Prefer templates matching this hotel's country; otherwise fall back to
      // country-agnostic templates, otherwise all of them.
      const hotelCountry = (hotelRecord?.COUNTRY || '').toUpperCase();
      const countrySpecific = hotelCountry
        ? templates.filter(t => (t.country || '').toUpperCase() === hotelCountry)
        : [];
      const defaults = templates.filter(t => !t.country);
      const candidates = countrySpecific.length > 0
        ? countrySpecific
        : (defaults.length > 0 ? defaults : templates);

      if (candidates.length === 1) {
        const acctTpl = await resolveHotelTemplate(candidates[0].canonical_group_id);
        setTemplateModal({ step: 'render', name: candidates[0].name, html: renderTemplate(candidates[0].content, enrichedRow), row: enrichedRow, header: renderHotelVars(sanitizeHtml(acctTpl?.header)), footer: renderHotelVars(sanitizeHtml(acctTpl?.footer)) });
        return;
      }
      // Several candidates and nothing to separate them — the user chooses, as before.
      setTemplateModal({ step: 'pick', row: enrichedRow, templates: candidates });
    } catch (err) {
      console.error('Failed to load templates:', err);
      setTemplateModal({ step: 'empty' });
    }
  };

  const handleTemplateSelect = async (template, row) => {
    const enrichedRow = await enrichRowForTemplate(row);
    const acctTpl = await resolveHotelTemplate(template.canonical_group_id);
    setTemplateModal({ step: 'render', name: template.name, html: renderTemplate(template.content, enrichedRow), row: enrichedRow, header: renderHotelVars(sanitizeHtml(acctTpl?.header)), footer: renderHotelVars(sanitizeHtml(acctTpl?.footer)) });
  };

  const openEmailModal = () => {
    if (!templateModal || templateModal.step !== 'render') return;
    const emailKey = Object.keys(templateModal.row || {}).find(k => k.toLowerCase() === 'email');
    const to = emailKey ? (templateModal.row[emailKey] || '') : '';
    const folioKey = Object.keys(templateModal.row || {}).find(k => k.toLowerCase() === 'foliono');
    const folioNo = folioKey ? templateModal.row[folioKey] : '';
    const subject = `${templateModal.name}${folioNo ? ` — Folio ${folioNo}` : ''}`;
    const header = templateModal.header || '';
    const footer = templateModal.footer || '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${header}${templateModal.html}${footer}</body></html>`;
    setEmailModal({ to, subject, html, sending: false, error: null, sent: false });
  };

  const handleSendEmail = async () => {
    if (!emailModal) return;
    setEmailModal(prev => ({ ...prev, sending: true, error: null }));
    try {
      await vaultsendemailService.send({ to: emailModal.to, subject: emailModal.subject, html: emailModal.html });
      setEmailModal(prev => ({ ...prev, sending: false, sent: true }));
    } catch (err) {
      setEmailModal(prev => ({ ...prev, sending: false, error: err.response?.data?.error?.message || err.message || 'Failed to send email' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHotelChange = (e) => {
    const selectedHotelId = e.target.value;
    setFormData(prev => ({ ...prev, hotelid: selectedHotelId ? parseInt(selectedHotelId) : '' }));
    if (selectedHotelId) {
      fetchHotel(selectedHotelId);
    } else {
      setHotelName('');
      setAccountName('');
      setAccountId('');
    }
  };

  const handleToggleActive = () => {
    setFormData(prev => ({
      ...prev,
      active: prev.active === 'Y' ? 'N' : 'Y'
    }));
  };

  const doCreateVault = async (data, apiExtract, paymentResult) => {
    const response = await vaultService.createVault(data);
    console.log('Vault created:', response);

    if (apiExtract && response.id) {
      await saveVaultSettings(response.id);
    }

    // Save group selections (default: all available groups)
    if (response.id && selectedGroupIds.length > 0) {
      try {
        await vaultsourcegroupService.setVaultGroupSelections(response.id, selectedGroupIds);
      } catch (err) {
        console.error('Failed to save group selections:', err);
      }
    }

    // Store subscription ID on the vault if applicable
    if (paymentResult?.type === 'subscription' && paymentResult.subscriptionId && response.id) {
      try {
        await vaultService.updateVault(response.id, { ...data, stripe_subscription_id: paymentResult.subscriptionId });
      } catch (err) {
        console.error('Failed to store subscription ID on vault:', err);
      }
    }

    if (response.id) {
      navigate(`/vault/${response.id}`);
    } else {
      const targetHotelId = effectiveHotelId || data.hotelid;
      navigate(targetHotelId ? `/hotel/${targetHotelId}` : '/partners');
    }
    return response;
  };

  const handleManageSuccess = (result) => {
    setPaymentModal(null);
    setSummaryLoadKey(k => k + 1);
  };

  const handlePaymentSuccess = async (paymentResult) => {
    setPaymentModal(null);
    if (!pendingSubmitData) return;
    setLoading(true);
    setError(null);
    try {
      const isInvoice = paymentResult?.type === 'invoice';
      const isSubscription = paymentResult?.type === 'subscription';
      const isFreeSeat = paymentResult?.type === 'free-seat';
      if (isEditMode && id) {
        // Vault already exists — record payment (or invoice) and advance wizard
        const effectiveAccountId = pendingSubmitData.effectiveAccountId || accountId || selectedAccount;
        const invoiceUpdates = isInvoice
          ? { invoice_enabled: 1, invoice_target: paymentResult.invoiceTarget }
          : { invoice_enabled: 0, invoice_target: '' }; // clear any pending invoice flag if paying by card or free seat
        const subscriptionUpdates = isSubscription
          ? { stripe_subscription_id: paymentResult.subscriptionId }
          : {};
        const freeSeatUpdates = isFreeSeat ? { use_free_seat: 1 } : {};
        const isOhip = pendingSubmitData.isApiExtract;
        // Free seat is treated like a paid card — advance immediately, no Stripe state.
        // Non-API (OperaV5) vaults go to client_generated so the Download App step is shown.
        const nextStatus = isInvoice ? 'payment' : (isOhip ? 'initiated' : 'client_generated');
        await vaultService.updateVault(id, { ...formData, status: nextStatus, ...invoiceUpdates, ...subscriptionUpdates, ...freeSeatUpdates });
        if (isInvoice) {
          await vaultPaymentService.createInvoice({
            vaultId: parseInt(id),
            vaultName: formData.name,
            targetType: paymentResult.invoiceTarget,
            hotelId: formData.hotelid,
            accountId: accountId || hotelRecord?.accountid || selectedAccount,
            stripePriceId: paymentResult.stripePriceId || null,
            retentionYears: parseRetentionYears(formData.minimum_retention_date),
            contactEmail: paymentResult.contactEmail || undefined,
          });
        }
        // OHIP vaults do NOT auto-extract after payment. The user must explicitly click
        // "Extract Records" on the Extraction step and complete the emailed verification code,
        // which triggers extraction via handleProcessVault (cost-acceptance gate).
        setFormData(prev => ({ ...prev, status: nextStatus, ...invoiceUpdates, ...subscriptionUpdates, ...freeSeatUpdates }));
        setSummaryLoadKey(k => k + 1);
        // Invoice → stay on Payment; OHIP → Extract; non-API (OperaV5) → Download App step.
        const postPaymentStep = isInvoice ? PAYMENT_STEP : (isOhip ? EXTRACT_STEP : DOWNLOAD_STEP);
        setWizardStep(postPaymentStep);
        vaultService.updateVault(id, { ...formData, status: nextStatus, ...invoiceUpdates, ...subscriptionUpdates, ...freeSeatUpdates, wizard_step: postPaymentStep }).catch(err =>
          console.error('Failed to persist wizard step after payment:', err)
        );
      } else {
        const formDataWithInvoice = isInvoice
          ? { ...pendingSubmitData.formData, invoice_enabled: 1, invoice_target: paymentResult.invoiceTarget }
          : pendingSubmitData.formData;
        const createdVault = await doCreateVault(formDataWithInvoice, pendingSubmitData.isApiExtract, isInvoice ? null : paymentResult);
        if (isInvoice && createdVault?.id) {
          await vaultPaymentService.createInvoice({
            vaultId: createdVault.id,
            vaultName: formDataWithInvoice.name,
            targetType: paymentResult.invoiceTarget,
            hotelId: formDataWithInvoice.hotelid,
            accountId: pendingSubmitData.effectiveAccountId || accountId || selectedAccount,
            stripePriceId: paymentResult.stripePriceId || null,
            retentionYears: parseRetentionYears(formDataWithInvoice.minimum_retention_date),
            contactEmail: paymentResult.contactEmail || undefined,
          });
        }
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      setError(err.response?.data?.message || err.message || 'Failed to process payment');
    } finally {
      setLoading(false);
      setPendingSubmitData(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.hotelid) {
      setError('Hotel ID is required');
      setLoading(false);
      return;
    }

    if (!formData.vaultsourceid) {
      setError('Vault System is required');
      setLoading(false);
      return;
    }

    if (!formData.extraction_start_date) {
      setError('Extraction start date is required');
      setLoading(false);
      return;
    }

    if (!formData.extraction_end_date) {
      setError('Extraction end date is required');
      setLoading(false);
      return;
    }

    if (!formData.minimum_retention_date) {
      setError('Minimum retention is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting vault data:', formData);

      if (isCreateMode) {
        await doCreateVault(formData, isApiExtract, null);
        return;
      } else if (isEditMode) {
        await vaultService.updateVault(id, formData);
        if (vaultSourceSettings.length > 0) {
          await saveVaultSettings(parseInt(id));
        }
        await fetchVault();
      }
    } catch (err) {
      console.error('Error saving vault:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save vault');
    } finally {
      setLoading(false);
    }
  };

  const saveVaultSettings = async (vaultId) => {
    const promises = Object.entries(vaultSettingValues).map(([sourceSettingId, value]) =>
      v2VaultSettingsService.upsertSetting({
        v2_vault_id: vaultId,
        v2_vault_source_setting_id: parseInt(sourceSettingId),
        setting_value: value || null
      })
    );
    await Promise.all(promises);
  };

  const handleCancel = () => {
    const targetHotelId = effectiveHotelId || formData.hotelid;
    if (targetHotelId) {
      navigate(`/hotel/${targetHotelId}`);
    } else {
      navigate('/partners');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this vault?')) {
      return;
    }

    try {
      setLoading(true);
      await vaultService.deleteVault(id, hotelid);
      const targetHotelId = effectiveHotelId || formData.hotelid;
      if (targetHotelId) {
        navigate(`/hotel/${targetHotelId}`);
      } else {
        navigate('/partners');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete vault');
      setLoading(false);
    }
  };

  const handleGenerateBinary = async (e) => {
    e.preventDefault();

    try {
      setError(null);
      setGenerateSuccess(null);
      setGenerating(true);
      setProgress(0);

      console.log('🚀 Generating binary for platform:', selectedPlatform);
      const response = await vaultdownloadsService.generateVaultDownload(formData.vaultid, selectedPlatform);
      const downloadId = response.data.download?.id;
      console.log('✅ Binary generation started, download ID:', downloadId);

      setGenerateSuccess(`App generation started for ${PLATFORMS[selectedPlatform]}. This may take several minutes.`);
      setGenerating(false);

      // Start progress bar animation
      if (downloadId) {
        setGeneratingDownloadId(downloadId);
      }

      // Refresh downloads list immediately to show the pending download
      console.log('🔄 Refreshing downloads list...');

      const vaultId = parseInt(formData.id);

      await fetchDownloads(vaultId);
      console.log('✅ Downloads list refreshed, polling should start automatically');
    } catch (err) {
      setGenerating(false);
      console.error('❌ Failed to generate binary:', err);
      setError(err.response?.data?.error?.message || 'Failed to start app generation');
    }
  };

  const handleDownloadFile = async (downloadId) => {
    try {
      const response = await vaultdownloadsService.getPresignedDownloadUrl(downloadId);
      const presignedUrl = response.data.presignedUrl;
      window.open(presignedUrl, '_blank');
      // Refresh so the row shows 'downloaded' status immediately
      const vaultId = parseInt(formData.id);
      if (vaultId && !isNaN(vaultId)) await fetchDownloads(vaultId);
    } catch (err) {
      console.error('Failed to get download URL:', err);
      setError('Failed to generate download URL: ' + (err.response?.data?.message || err.message));
    }
  };

  const platformFromDownloadName = (name) =>
    Object.keys(PLATFORMS).find(p => name && name.includes(p)) || null;

  const handleDeleteDownload = async (downloadId, downloadName) => {
    if (window.confirm(`Are you sure you want to delete "${downloadName}"?`)) {
      try {
        setError(null);
        setGenerateSuccess(null);
        await vaultdownloadsService.deleteVaultDownload(downloadId);
        setGenerateSuccess('Download deleted successfully');
        // Refresh the downloads list
        await fetchDownloads();
      } catch (err) {
        setError('Failed to delete download: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (createdAt) => {
    if (!createdAt) return { expired: true, text: 'N/A' };

    const created = new Date(createdAt).getTime();
    const expiresAt = created + (24 * 60 * 60 * 1000); // 24 hours in milliseconds
    const remaining = expiresAt - currentTime;

    if (remaining <= 0) {
      return { expired: true, text: 'Expired' };
    }

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    // Format as HH:MM:SS
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    const secondsStr = String(seconds).padStart(2, '0');

    return {
      expired: false,
      text: `${hoursStr}:${minutesStr}:${secondsStr}`,
      hours,
      minutes,
      seconds
    };
  };


  const handleProcessVault = async (reprocess = false) => {
    setProcessing(true);
    setProcessResult({ loading: true });
    try {
      const result = reprocess
        ? await v2VaultProcessService.reprocessVault(id)
        : await v2VaultProcessService.processVault(id);
      setProcessResult({ success: result.success, message: result.message });
      if (result.success) {
        if (reprocess) setFormData(prev => ({ ...prev, status: 'initiated' }));
        setPollingActive(true);
      }
    } catch (err) {
      setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
    } finally {
      setProcessing(false);
    }
  };

  // Loads one folder level (folders + files). `prefix` is a path relative to the vault root;
  // pass a `nextToken` to append the next page of files within the current folder.
  const loadUploadedFiles = async (prefix = '', nextToken = null) => {
    if (!id) return;
    setFilesLoading(true);
    setFilesError(null);
    setFilesMessage(null);
    try {
      const result = await v2VaultProcessService.listUploadedFiles(id, { prefix, nextToken });
      setFilesPrefix(result.relativePrefix ?? prefix);
      setFileFolders(result.folders || []);
      // Appending a page within a folder vs. loading a fresh folder.
      setUploadedFiles(prev => (nextToken ? [...(prev || []), ...(result.files || [])] : (result.files || [])));
      setFilesTruncated(!!result.truncated);
      setFilesNextToken(result.nextToken || null);
      // Surface the lambda's note (no bucket/prefix configured) only when the folder is truly empty.
      if ((result.files || []).length === 0 && (result.folders || []).length === 0 && result.message) setFilesMessage(result.message);
      if (result.bucket) {
        setUploadLocation({ bucket: result.bucket, prefix: result.prefix || null });
      }
    } catch (err) {
      // Keep uploadedFiles as null so the error state is not mistaken for a genuine empty list.
      console.error('Failed to load uploaded files:', err);
      setUploadedFiles(null);
      setFilesError(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to load uploaded files. Please try again.');
    } finally {
      setFilesLoading(false);
    }
  };

  // Breadcrumb segments for the current folder path (each with the relative prefix to navigate to).
  const filesBreadcrumb = filesPrefix
    ? filesPrefix.replace(/\/$/, '').split('/').map((seg, i, arr) => ({
        name: seg,
        prefix: arr.slice(0, i + 1).join('/') + '/',
      }))
    : [];

  // S3 extraction browser — shared by OperaV5 (binary upload) and OHIP (api_extract) vaults so both
  // show the contents of the extraction identically. Folder-navigable, paged, superadmin download.
  function renderFileBrowser() {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p style={{ color: '#374151', margin: 0 }}>
            {isApiExtract ? 'Records extracted from OHIP to S3.' : 'Files uploaded by the vault binary to S3.'}
          </p>
          <button
            type="button"
            className="pp-refresh-btn"
            onClick={() => loadUploadedFiles(filesPrefix)}
            disabled={filesLoading}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
            </svg>
            {filesLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {/* Breadcrumb — appears once you drill into a folder */}
        {!filesError && uploadedFiles !== null && (filesPrefix || fileFolders.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#6b7280' }}>
            <button type="button" onClick={() => loadUploadedFiles('')} disabled={filesLoading}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: filesPrefix ? '#4a6cf7' : '#374151', fontWeight: 600 }}>
              vault root
            </button>
            {filesBreadcrumb.map((seg, i) => (
              <span key={seg.prefix} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <span>/</span>
                <button type="button" onClick={() => loadUploadedFiles(seg.prefix)} disabled={filesLoading || i === filesBreadcrumb.length - 1}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: i === filesBreadcrumb.length - 1 ? 'default' : 'pointer', color: i === filesBreadcrumb.length - 1 ? '#374151' : '#4a6cf7', fontWeight: 600 }}>
                  {seg.name}
                </button>
              </span>
            ))}
          </div>
        )}
        {filesError ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '0.625rem 0.875rem', fontSize: '0.85rem' }}>{filesError}</div>
        ) : uploadedFiles === null ? (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Click <strong>Refresh</strong> to load the list of {isApiExtract ? 'extracted records' : 'uploaded files'}.
          </p>
        ) : (uploadedFiles.length === 0 && fileFolders.length === 0) ? (
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{filesMessage || (isApiExtract ? 'No records extracted yet.' : 'No files uploaded yet.')}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>File</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>Size</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem', color: '#374151', fontWeight: 600 }}>Uploaded</th>
                {isSuperAdmin && <th style={{ padding: '0.5rem 0.75rem' }} />}
              </tr>
            </thead>
            <tbody>
              {fileFolders.map((folder) => (
                <tr key={`dir:${folder.relativePrefix}`}
                    onClick={() => !filesLoading && loadUploadedFiles(folder.relativePrefix)}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: '#fff' }}>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#1e40af', fontWeight: 600, wordBreak: 'break-all' }}>
                    <span style={{ marginRight: 6 }}>📁</span>{folder.name}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', textAlign: 'right' }}>—</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#9ca3af', textAlign: 'right' }}>—</td>
                  {isSuperAdmin && <td />}
                </tr>
              ))}
              {uploadedFiles.map((f, i) => (
                <tr key={f.key} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#111827', wordBreak: 'break-all' }}>{f.name}</td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {f.size >= 1024 * 1024
                      ? `${(f.size / (1024 * 1024)).toFixed(1)} MB`
                      : f.size >= 1024
                      ? `${(f.size / 1024).toFixed(1)} KB`
                      : `${f.size} B`}
                  </td>
                  <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {new Date(f.lastModified).toLocaleString()}
                  </td>
                  {isSuperAdmin && (
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => handleDownloadUploadedFile(f.key)}
                        disabled={downloadingKey === f.key}
                        title="Download this file (superadmin)"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '0.3rem 0.7rem', fontSize: '0.8rem', fontWeight: 600,
                          color: '#fff', background: '#4a6cf7', border: 'none', borderRadius: 6,
                          cursor: downloadingKey === f.key ? 'default' : 'pointer',
                          opacity: downloadingKey === f.key ? 0.6 : 1,
                          outline: '1.5px dashed #a855f7', outlineOffset: 2,
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                          <path d="M8 2v8m0 0L5 7m3 3l3-3M3 13h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {downloadingKey === f.key ? 'Preparing…' : 'Download'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!filesError && filesTruncated && (
          <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
            <button type="button" className="pp-refresh-btn" disabled={filesLoading}
              onClick={() => loadUploadedFiles(filesPrefix, filesNextToken)}>
              {filesLoading ? 'Loading…' : 'Load more files'}
            </button>
          </div>
        )}
      </>
    );
  }

  // Superadmin only: fetch a short-lived presigned URL for one uploaded file and
  // trigger the browser download.
  const [downloadingKey, setDownloadingKey] = useState(null);
  const handleDownloadUploadedFile = async (key) => {
    setDownloadingKey(key);
    try {
      const { url } = await v2VaultProcessService.getFileDownloadUrl(id, key);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setDownloadingKey(null);
    }
  };

  // Open the OHIP extract verification flow (warning → code email → confirm)
  // reprocessType: null = initial extract, 'full' = reprocess vault, 'skipExtraction' = reprocess skip, 'pdfsOnly' = PDFs only
  const openOhipExtractModal = (reprocessType = null) => {
    setOhipExtractModal({ reprocessType, step: 'warning', codeValue: '', sending: false, confirming: false, error: null, sentEmail: null });
  };

  const handleOhipSendCode = async () => {
    setOhipExtractModal(prev => ({ ...prev, sending: true, error: null }));
    try {
      const result = await v2VaultOhipTestService.sendExtractCode(parseInt(id));
      setOhipExtractModal(prev => ({ ...prev, sending: false, step: 'code_sent', sentEmail: result.data?.email || null }));
    } catch (err) {
      setOhipExtractModal(prev => ({ ...prev, sending: false, error: err.response?.data?.message || 'Failed to send code' }));
    }
  };

  // Runs the actual extract/reprocess operation once the cost-acceptance gate has been cleared
  // (either by a verified code, or — superadmin only — by skipping verification).
  const runExtractOperation = async (reprocessType) => {
    setOhipExtractModal(null);
    if (reprocessType === null) {
      await handleProcessVault(false);
    } else if (reprocessType === 'full') {
      await handleProcessVault(true);
    } else if (reprocessType === 'skipExtraction') {
      setProcessing(true);
      setProcessResult({ loading: true });
      try {
        const result = await v2VaultProcessService.reprocessSkipExtraction(id);
        setProcessResult({ success: result.success, message: result.message });
        if (result.success) setPollingActive(true);
      } catch (err) {
        setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
      } finally {
        setProcessing(false);
      }
    } else if (reprocessType === 'pdfsOnly') {
      setProcessing(true);
      setProcessResult({ loading: true });
      try {
        const result = await v2VaultProcessService.reprocessPdfsOnly(id);
        setProcessResult({ success: result.success, message: result.message });
      } catch (err) {
        setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleOhipConfirmCode = async () => {
    const modal = ohipExtractModal;
    if (!modal) return;
    setOhipExtractModal(prev => ({ ...prev, confirming: true, error: null }));
    try {
      await v2VaultOhipTestService.confirmExtractCode(parseInt(id), modal.codeValue);
      await runExtractOperation(modal.reprocessType);
    } catch (err) {
      setOhipExtractModal(prev => ({ ...prev, confirming: false, error: err.response?.data?.message || 'Invalid or expired code' }));
    }
  };

  // Superadmin-only: bypass the emailed verification code and extract immediately.
  const handleOhipSkipVerification = async () => {
    if (!ohipExtractModal) return;
    await runExtractOperation(ohipExtractModal.reprocessType);
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="vault-form-container">Loading vault...</div>;
  }

  if (isCreateMode && !stripeReady) {
    return (
      <div className="vault-form-container">
        <div className="error-message">Your partner account must have Stripe connected before you can create vaults.</div>
      </div>
    );
  }

  if (isCreateMode && !hasVaultProducts) {
    return (
      <div className="vault-form-container">
        <div className="error-message">No vault products are available for your partner. Contact hconnect to set up vault products before creating a vault.</div>
      </div>
    );
  }

  if (isCreateMode && !effectiveHotelId) {
    return (
      <div className="vault-form-container">
        <h2>Create New Vault</h2>
        {/* Shared picker (self-fetching, scoped to the selected chain or partner). Selecting a hotel
            reloads this form at the hotel-scoped vault-create route. */}
        <WizardHotelStep
          title="Select Hotel"
          subtitle="Select the hotel this vault belongs to."
          accountId={selectedAccount}
          partnerId={selectedPartner}
          selectedHotel={null}
          onSelect={(h) => navigate(`/hotel/${h.hotelid}/vault/new`)}
        />
      </div>
    );
  }

  const advanceWizardStep = async () => {
    if (isEditMode && wizardStep === 1) {
      try {
        setLoading(true);
        await vaultService.updateVault(id, formData);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to save');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    if (wizardStep === GROUP_STEP) {
      if (selectedGroupIds.length === 0) {
        setError('Please select at least one data group.');
        return;
      }
      try {
        setLoading(true);
        await vaultsourcegroupService.setVaultGroupSelections(id, selectedGroupIds);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to save group selections');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    if (isEditMode && wizardStep === SETTINGS_STEP) {
      try {
        setLoading(true);
        if (isApiExtract) {
          if (vaultSourceSettings.length > 0) await saveVaultSettings(parseInt(id));
        } else {
          await vaultService.updateVault(id, formData);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to save');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    const nextStep = Math.min(READY_STEP, wizardStep + 1);
    setWizardStep(nextStep);
    if (id) {
      vaultService.updateVault(id, { ...formData, wizard_step: nextStep }).catch(err =>
        console.error('Failed to persist wizard step:', err)
      );
    }
  };

  return (
    <div className="vault-form-container">
      {paymentModal && (
        <VaultPaymentModal
          hotelId={paymentModal.hotelId}
          accountId={paymentModal.accountId}
          accountName={accountName}
          vaultId={id ? parseInt(id) : null}
          invoicePrivilege={isSuperAdmin || isPartner ? 'partner' : isChainAdmin ? 'chain' : 'hotel'}
          invoiceTarget={formData.invoice_target || 'hotel'}
          retentionYears={parseRetentionYears(formData.minimum_retention_date) || null}
          isSuperAdmin={isSuperAdmin}
          currentUser={user}
          subscriptionId={paymentModal.subscriptionId || null}
          onSuccess={paymentModal.subscriptionId ? handleManageSuccess : handlePaymentSuccess}
          onCancel={() => { setPaymentModal(null); setPendingSubmitData(null); }}
        />
      )}

      {/* ── OHIP Extract Verification Modal ── */}
      {ohipExtractModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', maxWidth: '520px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {ohipExtractModal.step === 'warning' ? (
              <>
                <h3 style={{ margin: '0 0 16px', color: '#1e3a5f', fontSize: '18px' }}>OHIP Extraction — Cost Warning</h3>
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                  <strong style={{ color: '#856404', display: 'block', marginBottom: '8px' }}>⚠️ This action will incur API costs</strong>
                  <p style={{ color: '#856404', margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                    Extracting records from OHIP consumes API calls against your OHIP subscription.
                    Each call may be charged as per your OHIP terms and conditions.
                    Please ensure you have reviewed the estimated cost before proceeding.
                  </p>
                </div>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '24px' }}>
                  To confirm you understand and accept these potential costs, a 6-digit verification code will be sent to <strong>{user?.email || user?.username || 'your email address'}</strong>.
                </p>
                {ohipExtractModal.error && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 14px', color: '#991b1b', fontSize: '13px', marginBottom: '16px' }}>
                    {ohipExtractModal.error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px', justifyContent: isSuperAdmin ? 'space-between' : 'flex-end', alignItems: 'center' }}>
                  {isSuperAdmin && (
                    <button type="button" className="btn btn-secondary sa-only" onClick={handleOhipSkipVerification} disabled={ohipExtractModal.sending}>
                      Skip verification &amp; extract
                    </button>
                  )}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setOhipExtractModal(null)} disabled={ohipExtractModal.sending}>Cancel</button>
                    <button type="button" className="btn-process" onClick={handleOhipSendCode} disabled={ohipExtractModal.sending}>
                      {ohipExtractModal.sending ? 'Sending...' : 'Send Verification Code'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 16px', color: '#1e3a5f', fontSize: '18px' }}>Enter Verification Code</h3>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '20px' }}>
                  A 6-digit code has been sent to {ohipExtractModal.sentEmail ? <strong>{ohipExtractModal.sentEmail}</strong> : 'your email address'}. Enter it below to proceed with the extraction.
                </p>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={ohipExtractModal.codeValue}
                    onChange={e => setOhipExtractModal(prev => ({ ...prev, codeValue: e.target.value.replace(/\D/g, '') }))}
                    style={{ width: '100%', textAlign: 'center', fontSize: '28px', letterSpacing: '10px', padding: '12px', border: '2px solid #d1d5db', borderRadius: '8px', outline: 'none' }}
                    autoFocus
                  />
                </div>
                {ohipExtractModal.error && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 14px', color: '#991b1b', fontSize: '13px', marginBottom: '16px' }}>
                    {ohipExtractModal.error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button type="button" style={{ fontSize: '13px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => setOhipExtractModal(prev => ({ ...prev, step: 'warning', error: null }))}
                    disabled={ohipExtractModal.confirming}>
                    ← Back
                  </button>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setOhipExtractModal(null)} disabled={ohipExtractModal.confirming}>Cancel</button>
                    <button type="button" className="btn-process" onClick={handleOhipConfirmCode}
                      disabled={ohipExtractModal.confirming || ohipExtractModal.codeValue.length !== 6}>
                      {ohipExtractModal.confirming ? 'Verifying...' : 'Confirm & Extract'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── VAULT READY PAGE ── completely new layout when vault is ready ── */}
      {(isViewMode || isEditMode) && ['vault_ready','payment_missed','subscription_cancelled','retention_expired'].includes(formData.status) && !settingsOpen ? (() => {
        const summaryHasPayments = (paymentSummary?.subscriptions?.length > 0) || (paymentSummary?.invoices?.length > 0);
        // Superadmins are never payment-blocked — they need to access / reprocess vaults for support.
        const accessBlocked = !isSuperAdmin && (formData.status !== 'vault_ready' || (summaryHasPayments && !paymentSummary?.hasActivePayment));
        const canSeeSettings = isSuperAdmin || isChainAdmin || !isPartner;
        const vaultGroups = availableGroups.filter(g => selectedGroupIds.includes(g.id));
        return (
          <div className="vault-ready-page">
            {/* Page header */}
            <div className="vault-ready-page-header">
              <div>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#111827' }}>{formData.name || 'Vault'}</h2>
                {(accountName || hotelName) && (
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                    {[accountName, hotelName].filter(Boolean).join(' / ')}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {canSeeSettings && (
                  <button
                    type="button"
                    onClick={() => { setSettingsOpen(true); setWizardStep(READY_STEP); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontWeight: 500, color: '#374151', cursor: 'pointer' }}
                  >
                    <span className="material-icons" style={{ fontSize: '16px' }}>settings</span>
                    Settings
                  </button>
                )}
                <button type="button" onClick={handleCancel} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                  Back to Hotel
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Payment blocked warning */}
            {accessBlocked && (() => {
              const statusMessages = {
                payment_missed: { title: '⛔ Payment missed — vault search is disabled', body: 'A subscription payment has failed. Please update your payment method to restore access.' },
                subscription_cancelled: { title: '⛔ Subscription cancelled — vault search is disabled', body: 'Your subscription has been cancelled. Please set up a new payment to restore access.' },
                retention_expired: { title: '⛔ Retention period expired — vault search is disabled', body: 'The retention period for this vault has ended. Please set up a new payment to extend your retention period.' },
              };
              const msg = statusMessages[formData.status] || { title: '⛔ Vault search is disabled — no active payment', body: 'All subscriptions are cancelled or no valid payment is on record.' };
              return (
                <div style={{ marginBottom: '1.5rem', padding: '16px 18px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '13px' }}>
                  <strong style={{ fontSize: '14px' }}>{msg.title}</strong>
                  <p style={{ margin: '8px 0 12px' }}>{msg.body}</p>
                  <button type="button" className="btn-process" onClick={() => { setSettingsOpen(true); setWizardStep(PAYMENT_STEP); }}>
                    Go to Payment
                  </button>
                </div>
              );
            })()}

            {/* Data group cards */}
            {vaultGroups.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '2rem' }}>
                {vaultGroups.map(group => {
                  const isSelected = selectedGroup?.id === group.id;
                  return (
                    <div
                      key={group.id}
                      onClick={() => handleGroupSelect(group)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', padding: '20px 18px', background: isSelected ? '#eef2ff' : '#fff', border: `2px solid ${isSelected ? '#4f46e5' : '#e2e8f0'}`, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' }}
                    >
                      {group.icon ? (
                        <span className="material-icons" style={{ fontSize: '36px', color: '#4f46e5' }}>{group.icon}</span>
                      ) : (
                        <span style={{ fontSize: '36px' }}>📁</span>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '15px', color: '#111827', marginBottom: '5px' }}>{group.name}</div>
                        {group.description && (
                          <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.6' }}>{group.description}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Search workspace */}
            {!accessBlocked && (
              <div className="query-workspace">
                {vaultGroups.length === 0 && (
                  <div className="sources-dropdown-bar">
                    <label htmlFor="source-group-select">Source</label>
                    <select
                      id="source-group-select"
                      className="source-group-select"
                      value={selectedGroup?.id || ''}
                      onChange={(e) => {
                        const group = sourceGroups.find(g => g.id === parseInt(e.target.value));
                        if (group) handleGroupSelect(group);
                      }}
                      disabled={sourceGroups.length === 0}
                    >
                      <option value="">
                        {sourceGroups.length === 0 ? 'No source groups available' : '— Select a source group —'}
                      </option>
                      {sourceGroups.map((group) => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {isSuperAdmin && (
                  <div className="sa-only-panel" style={{ marginBottom: '0.75rem', padding: '0.625rem 0.875rem', borderRadius: 8, fontSize: '0.8rem', color: '#374151' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#6b21a8' }}>Vault database: </span>
                      <code style={{ fontSize: '0.8rem', color: '#1e293b' }}>{searchResult?.data?.athenaDatabase || `vault_${id}`}</code>
                    </div>
                    <div style={{ marginTop: '0.35rem' }}>
                      <span style={{ fontWeight: 600, color: '#6b21a8' }}>Uploaded files: </span>
                      {derivedS3Location?.bucket && derivedS3Location?.prefix
                        ? <code style={{ fontSize: '0.8rem', color: '#1e293b' }}>s3://{derivedS3Location.bucket}/{derivedS3Location.prefix}/</code>
                        : <span style={{ color: '#6b7280' }}>not available — S3 prefix not assigned</span>}
                    </div>
                    <div style={{ marginTop: '0.35rem' }}>
                      <span style={{ fontWeight: 600, color: '#6b21a8' }}>Processed files: </span>
                      {(() => {
                        const loc = searchResult?.data?.processedLocation
                          || (derivedS3Location?.bucket && derivedS3Location?.prefix
                              ? `s3://${derivedS3Location.bucket.replace(/-upload$/, '-processed')}/${derivedS3Location.prefix}/`
                              : null);
                        return loc
                          ? <code style={{ fontSize: '0.8rem', color: '#1e293b' }}>{loc}</code>
                          : <span style={{ color: '#6b7280' }}>not available — S3 prefix not assigned</span>;
                      })()}
                    </div>
                  </div>
                )}
                <div className="query-panel">
                  <div className="sql-viewer-container">
                    {!selectedGroup ? (
                      <div className="no-tables"><p>Select a source group to view its SQL queries</p></div>
                    ) : groupSqlsLoading ? (
                      <div className="no-tables"><p>Loading SQL queries...</p></div>
                    ) : groupSqls.length === 0 ? (
                      <div className="no-tables"><p>No SQL queries configured for <strong>{selectedGroup.name}</strong></p></div>
                    ) : (
                      <div className="sql-viewer">
                        <div className="sources-dropdown-bar">
                          <label htmlFor="sql-select">Query</label>
                          <select
                            id="sql-select"
                            className="source-group-select"
                            value={expandedSqlId || ''}
                            onChange={(e) => handleSqlSelect(e.target.value ? parseInt(e.target.value) : null)}
                          >
                            <option value="">— Select a query —</option>
                            {groupSqls.map((sql) => (
                              <option key={sql.id} value={sql.id}>{sql.name}</option>
                            ))}
                          </select>
                        </div>
                        {expandedSqlId && (() => {
                          const sql = groupSqls.find(s => s.id === expandedSqlId);
                          if (!sql) return null;
                          if (sql.parameters === undefined) return <p className="search-results-loading">Loading...</p>;
                          return (
                            <>
                              {sql.parameters.length > 0 && (
                                <div className="sql-entry-params">
                                  <div className="sql-params-list">
                                    {sql.parameters.map((param) => (
                                      <div key={param.id} className="sql-param-row">
                                        <label className="sql-param-label" title={param.description || param.name}>{param.name}</label>
                                        <input
                                          type={param.data_type === 'date' ? 'date' : 'text'}
                                          className="sql-param-input"
                                          value={paramValues[sql.id]?.[param.name] ?? ''}
                                          onChange={(e) => handleParamChange(sql.id, param.name, e.target.value)}
                                          placeholder={param.data_type === 'date' ? undefined : (param.description || `Value for ${param.name}`)}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="sql-entry-actions">
                                <button type="button" className="sql-search-btn" onClick={() => handleSqlSearch(sql)} disabled={searchResult?.loading}>
                                  {searchResult?.loading && searchResult?.sqlName === sql.name ? '⏳ Searching...' : '🔍 Search'}
                                </button>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                  {searchResult && (
                    <div className="search-results-panel">
                      <div className="search-results-header"><h4>{searchResult.sqlName}</h4></div>
                      <div className="search-results-body">
                        {searchResult.loading && <p className="search-results-loading">Searching...</p>}
                        {searchResult.error && <p className="search-results-error">{searchResult.error}</p>}
                        {searchResult.data && searchResult.data.rows?.length > 0 ? (
                          <div className="search-results-table-wrap">
                            <table className="search-results-table">
                              <thead>
                                <tr>
                                  {pickResultDisplayColumns(searchResult.data.columns)
                                    .map((col, i) => <th key={i}>{col}</th>)}
                                  <th className="result-actions"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {searchResult.data.rows.map((row, ri) => {
                                  const resIdKey = Object.keys(row).find(k => k.toLowerCase() === 'reservationid');
                                  const reservationId = resIdKey ? row[resIdKey] : null;
                                  const s3KeyCol = Object.keys(row).find(k => k.toLowerCase() === 's3_key');
                                  const folioS3Key = s3KeyCol ? row[s3KeyCol] : null;
                                  const hasPdf = isApiExtract ? !!folioS3Key : !!reservationId;
                                  const displayCols = pickResultDisplayColumns(searchResult.data.columns);
                                  return (
                                    <tr key={ri} onClick={() => handleRowClick(row)} className="clickable-result-row">
                                      {displayCols.map((col, ci) => {
                                        const val = row[col];
                                        return <td key={ci} title={val != null ? String(val) : ''}>{val != null ? String(val) : ''}</td>;
                                      })}
                                      <td className="result-actions" onClick={e => e.stopPropagation()}>
                                        {/* Details opens the shared folio view — the same modal, the same
                                            fields and the same Print/Download for every vault type. On
                                            OperaV5 this is the button that was missing: clicking the row
                                            went to the canonical-template path instead, so there was no
                                            way to see a folio's full field set the way an OHIP vault
                                            shows it. Rendered for BOTH types deliberately; the row click
                                            still works, but a button is discoverable. */}
                                        <button type="button" className="btn-payload"
                                          onClick={() => openFolioDetail(row)}>Details</button>
                                        {hasPdf && (
                                          <button type="button" className="btn-pdf" onClick={async () => {
                                            try {
                                              // Row-resolved, so this one call serves both PMS types.
                                              const result = await vaultfoliourlService.getDocumentUrlForRow(id, row);
                                              window.open(result.url, '_blank');
                                            } catch (e) {
                                              alert('PDF not available: ' + (e.response?.data?.error?.message || e.message));
                                            }
                                          }}>PDF</button>
                                        )}
                                        {/* Payload — superadmin only, and now for EVERY vault type.
                                            It shows the CANONICAL row: the exact object the folio
                                            template renders from, which is what ends up in the PDF. On
                                            OHIP that object also carries the source `payload` column, so
                                            nothing is lost by showing the canonical form instead — the
                                            raw folio JSON is still in there, parsed rather than escaped.
                                            On OperaV5 there is no payload column at all, which is why
                                            this button did not appear and there was no way to see what
                                            the template was given. */}
                                        {isSuperAdmin && (
                                          <button type="button" className="btn-payload"
                                            onClick={() => openCanonicalPayload(row)}>Payload</button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : searchResult.data && !searchResult.loading ? (
                          <>
                            <p className="search-results-empty">No results returned</p>
                            {isSuperAdmin && searchResult.data?.sql && <pre className="search-results-sql">{searchResult.data.sql}</pre>}
                          </>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })() : (
      /* ── WIZARD LAYOUT — create / edit / settings mode ── */
      <>

      {/* Settings mode heading */}
      {(isViewMode || isEditMode) && settingsOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <button
            type="button"
            onClick={() => { setSettingsOpen(false); setWizardStep(READY_STEP); }}
            style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
          >
            ← Close Settings
          </button>
          <h2 style={{ margin: 0 }}>{formData.name || 'Vault'} — Settings</h2>
        </div>
      )}

      {(isViewMode || isEditMode) && !settingsOpen && <h2>{formData.name || 'Vault'}</h2>}

      {(isCreateMode || isEditMode) && (
        <h2>
          {isCreateMode && 'Create New Vault'}
          {isEditMode && 'Edit Vault'}
        </h2>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Vault Setup Wizard Header */}
      <div className="vault-wizard-header">
        {wizardSteps.map((step, i) => {
          const stepNum = i + 1;
          const isCompleted = (isViewMode || isEditMode) && wizardStep > stepNum;
          const isCurrent = isCreateMode ? stepNum === 1 : wizardStep === stepNum;
          return (
            <React.Fragment key={step.label}>
              <div className={`wizard-step-item${isCurrent ? ' active' : ''}${isCompleted ? ' completed' : ''}`}>
                <div className="wizard-step-circle">{isCompleted ? '✓' : stepNum}</div>
                <div className="wizard-step-label">{step.label}</div>
              </div>
              {i < wizardSteps.length - 1 && (
                <div className={`wizard-step-connector${isCompleted ? ' completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {formData.status === 'error' && (isViewMode || isEditMode) && (
        <div className="progress-error-banner">
          ❌ Extraction failed — reprocess the vault to try again
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Basic Information - create mode only; view/edit shows these inside the Details tab */}
        {isCreateMode && (
        <div className="form-section">
          {hotelName && (
            <div className="form-group">
              <label>Hotel</label>
              <input type="text" value={hotelName} disabled style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="name">
              Vault Name {isCreateMode && <span style={{ fontSize: '0.85em', color: '#666' }}>(Auto-generated)</span>}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name || (isCreateMode ? 'Will be auto-generated from Partner-Account-Hotel-Source' : '')}
              onChange={handleChange}
              disabled={true}
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
              placeholder={isCreateMode ? 'Auto-generated on save' : ''}
            />
            {isCreateMode && (
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                The vault name will be automatically generated as: Partner Name-Account Name-Hotel Name-Vault System
              </small>
            )}
          </div>


          <div className="form-group">
            <label htmlFor="vaultsourceid">Vault System *</label>
            <select
              id="vaultsourceid"
              name="vaultsourceid"
              value={formData.vaultsourceid || ''}
              onChange={handleChange}
              disabled={isViewMode}
              required
            >
              <option value="">-- Select Vault System --</option>
              {vaultsources.map((vaultsource) => (
                <option key={vaultsource.id} value={vaultsource.id}>
                  {vaultsource.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="minimum_retention_date">Minimum Retention (years) <span className="required">*</span></label>
            {formData.status !== 'initiated' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.95rem', color: '#374151', fontWeight: 600 }}>{formData.minimum_retention_date} year{formData.minimum_retention_date !== '1' ? 's' : ''}</span>
                <span style={{ fontSize: '0.8rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>🔒 locked after payment</span>
              </div>
            ) : (
              <select
                id="minimum_retention_date"
                name="minimum_retention_date"
                value={formData.minimum_retention_date || ''}
                onChange={handleChange}
                disabled={isViewMode}
              >
                <option value="">— select —</option>
                {Array.from({ length: 18 }, (_, i) => i + 3).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
              disabled={isViewMode}
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="extraction_start_date_create">Extraction Start Date <span className="required">*</span></label>
            <input
              type="date"
              id="extraction_start_date_create"
              name="extraction_start_date"
              value={formData.extraction_start_date || ''}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="extraction_end_date_create">Extraction End Date <span className="required">*</span></label>
            <input
              type="date"
              id="extraction_end_date_create"
              name="extraction_end_date"
              value={formData.extraction_end_date || ''}
              onChange={handleChange}
              required
            />
          </div>


        </div>
        )}

        {/* Wizard Step Content - view/edit mode */}
        {(isViewMode || isEditMode) && id && (
          <div className="wizard-step-content">
            <div className="tab-content-area">
              {/* Step 1: Vault Details */}
              {wizardStep === 1 && (
                <div className="wizard-panel">
                  <div className="form-section">
                    {hotelName && (
                      <div className="form-group">
                        <label>Hotel</label>
                        <input type="text" value={hotelName} disabled style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }} />
                      </div>
                    )}
                    <div className="form-group">
                      <label htmlFor="name-view">Vault Name</label>
                      <input
                        type="text"
                        id="name-view"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleChange}
                        disabled={isViewMode}
                        style={isViewMode ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="vaultsourceid-view">Vault System</label>
                      <select
                        id="vaultsourceid-view"
                        name="vaultsourceid"
                        value={formData.vaultsourceid || ''}
                        onChange={handleChange}
                        disabled={isViewMode}
                      >
                        <option value="">-- Select Vault System --</option>
                        {vaultsources.map((vs) => (
                          <option key={vs.id} value={vs.id}>{vs.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="minimum_retention_date-view">Minimum Retention (years) <span className="required">*</span></label>
                      {formData.status !== 'initiated' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.95rem', color: '#374151', fontWeight: 600 }}>{formData.minimum_retention_date} year{formData.minimum_retention_date !== '1' ? 's' : ''}</span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: '4px' }}>🔒 locked after payment</span>
                        </div>
                      ) : (
                        <select
                          id="minimum_retention_date-view"
                          name="minimum_retention_date"
                          value={formData.minimum_retention_date || ''}
                          onChange={handleChange}
                          disabled={isViewMode}
                        >
                          <option value="">— select —</option>
                          {Array.from({ length: 18 }, (_, i) => i + 3).map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="form-group full-width">
                      <label htmlFor="description-view">Description</label>
                      <textarea
                        id="description-view"
                        name="description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        disabled={isViewMode}
                        rows="4"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="extraction_start_date">Extraction Start Date <span className="required">*</span></label>
                      <input type="date" id="extraction_start_date" name="extraction_start_date" value={formData.extraction_start_date || ''} onChange={handleChange} disabled={isViewMode} required />
                    </div>
                    <div className="form-group">
                      <label htmlFor="extraction_end_date">Extraction End Date <span className="required">*</span></label>
                      <input type="date" id="extraction_end_date" name="extraction_end_date" value={formData.extraction_end_date || ''} onChange={handleChange} disabled={isViewMode} required />
                    </div>
                  </div>


                </div>
              )}

              {/* Step 3: API Settings (API type) */}
              {wizardStep === SETTINGS_STEP && isApiExtract && (
                <div className="wizard-panel">
                  <div className="form-section">

                    {/* Credential choice — uses the shared CredentialsPicker */}
                    {!isViewMode && (() => {
                      const pickerSelection = formData.credential_entity_type
                        ? { credential_id: formData.credential_id }
                        : null;
                      const onPick = (cred, values) => {
                        applyVaultOverrides(vaultSourceSettings, values, setVaultSettingValues);
                        setFormData(prev => ({
                          ...prev,
                          credential_entity_type: cred.entity_type,
                          credential_entity_id:   cred.entity_id,
                          credential_id:          cred.credential_id,
                          credential_name:        cred.credential_name || prev.credential_name,
                          use_hconnect_credentials: 0,
                        }));
                        setEditingCred(false);
                        setOhipTestResult(null);
                        setSettingsChangedSinceTest(true);
                        setEstimation(null);
                      };
                      const hasEntityCred = formData.credential_entity_type && formData.credential_id;
                      const clearCred = () => {
                        setFormData(prev => ({ ...prev, credential_entity_type: null, credential_entity_id: null, credential_id: null, credential_name: null, use_hconnect_credentials: 0 }));
                        setEditingCred(false);
                        setOhipTestResult(null);
                        setSettingsChangedSinceTest(true);
                        setEstimation(null);
                      };

                      // The shared selector: chip when chosen, "+ Add Credential" when not, full-screen
                      // picker behind both. Open state is driven by editingCred so the surrounding form
                      // keeps control of when the picker is showing.
                      return (
                        <div className="vault-cred-section">
                          <div className="vault-cred-header"><div className="vault-cred-title">Connection Credentials</div></div>
                          <CredentialSelector
                            marketplaceId={37 /* OHIP_MARKETPLACE_ID */}
                            context="vault"
                            contextId={id}
                            hotelId={formData.hotelid}
                            canEdit={isSuperAdmin}
                            title={null}
                            subtitle="How would you like to connect to OHIP?"
                            value={hasEntityCred ? {
                              credential_name: formData.credential_name || 'Credential',
                              _entityType: formData.credential_entity_type,
                              credential_id: formData.credential_id,
                            } : null}
                            pickerValue={pickerSelection}
                            // The per-hotel fields (HOTEL_ID on a chain/partner OHIP credential) are asked IN the
                            // picker with Save, as on every product — and land in this vault's source settings.
                            overridesInPicker
                            overrides={vaultOverrideValues(vaultSourceSettings, vaultSettingValues)}
                            overridesTitle="For this hotel"
                            onChange={(sel, values) => (sel ? onPick(sel, values) : clearCred())}
                            open={editingCred}
                            onOpenChange={setEditingCred}
                          />
                        </div>
                      );
                    })()}

                    {/* Per-instance overrides, READ ONLY in view mode — they are entered in the picker otherwise. */}
                    {isViewMode && <CredentialOverrides
                      marketplaceId={37 /* OHIP_MARKETPLACE_ID */}
                      level={formData.credential_entity_type}
                      activeLevels={['account', 'partner']}
                      disabled
                      values={vaultOverrideValues(vaultSourceSettings, vaultSettingValues)}
                      onChange={(name, val) => {
                        const row = findVaultSettingRow(vaultSourceSettings, name);
                        if (!row) return;
                        setVaultSettingValues(prev => ({ ...prev, [row.id]: val }));
                        setOhipTestResult(null);
                        setSettingsChangedSinceTest(true);
                        setEstimation(null);
                      }}
                    />}

                    {!isViewMode && (
                      <div style={{ marginTop: '1rem' }}>
                        {settingsChangedSinceTest && (
                          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.875rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e', fontWeight: 500 }}>
                            ⚠️ Settings have changed — you must re-test the connection before proceeding.
                          </div>
                        )}
                        {!ohipTestResult && !settingsChangedSinceTest && (
                          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.875rem', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e', fontWeight: 500 }}>
                            ⚠️ You must test the OHIP connection before proceeding.
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={async () => {
                              if (vaultSourceSettings.length > 0) await saveVaultSettings(parseInt(id));
                              // Save use_hconnect_credentials to vault before testing
                              await vaultService.updateVault(id, { ...formData });
                              setOhipTestResult({ loading: true });
                              setEstimation(null);
                              try {
                                const result = await v2VaultOhipTestService.testConnection(parseInt(id));
                                setOhipTestResult(result);
                                if (result.success) {
                                  setSettingsChangedSinceTest(false);
                                  // Auto-run estimation after successful test
                                  setEstimating(true);
                                  try {
                                    const est = await v2VaultOhipTestService.estimateRecords(parseInt(id));
                                    setEstimation(est);
                                  } catch (estErr) {
                                    setEstimation({ error: estErr.response?.data?.message || estErr.message || 'Estimation failed' });
                                  } finally {
                                    setEstimating(false);
                                  }
                                }
                              } catch (err) {
                                setOhipTestResult({ success: false, message: err.response?.data?.error?.message || err.response?.data?.message || err.message || 'Test failed' });
                              }
                            }}
                            disabled={ohipTestResult?.loading || estimating}
                            style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', fontWeight: 600, cursor: 'pointer' }}
                          >
                            {ohipTestResult?.loading ? 'Testing...' : settingsChangedSinceTest ? 'Re-test Connection' : 'Test OHIP Connection'}
                          </button>
                          {ohipTestResult && !ohipTestResult.loading && (
                            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: ohipTestResult.success ? '#16a34a' : '#dc2626' }}>
                              {ohipTestResult.success ? '✓' : '✗'} {ohipTestResult.message}
                              {ohipTestResult.persisted && !settingsChangedSinceTest && (
                                <span style={{ fontSize: '0.78rem', color: '#6b7280', marginLeft: '0.4rem' }}>(last saved result)</span>
                              )}
                            </span>
                          )}
                        </div>

                        {/* Estimation result */}
                        {estimating && (
                          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.875rem', color: '#6b7280' }}>
                            ⏳ Estimating records from OHIP...
                          </div>
                        )}
                        {estimation && !estimating && (
                          <div style={{ marginTop: '1rem', padding: '1rem 1.25rem', background: estimation.error ? '#fef2f2' : (estimation.estimatedCost > 0 ? '#fffbeb' : '#f0fdf4'), border: `1px solid ${estimation.error ? '#fca5a5' : (estimation.estimatedCost > 0 ? '#fde68a' : '#86efac')}`, borderRadius: '8px' }}>
                            {estimation.error ? (
                              <div style={{ color: '#dc2626', fontSize: '0.875rem' }}>⚠️ Could not estimate records: {estimation.error}</div>
                            ) : (
                              <>
                                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.25rem', color: '#111827' }}>📊 OHIP Record Estimate</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.6rem' }}>Only reservations that have <strong>checked out</strong> are extracted — in‑house, arriving, no‑show and cancelled reservations are excluded.</div>
                                <div style={{ fontSize: '0.875rem', color: '#374151', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 1.5rem' }}>
                                  <span>Checked‑out reservations in range:</span><span style={{ fontWeight: 600 }}>{estimation.reservationCount?.toLocaleString()}</span>
                                  <span>Estimated API calls:</span><span style={{ fontWeight: 600 }}>{estimation.totalApiCalls?.toLocaleString()}</span>
                                  {estimation.groupEstimates?.map(g => (
                                    <React.Fragment key={g.dataType}>
                                      <span style={{ color: '#6b7280' }}>— {g.dataType}:</span>
                                      <span style={{ color: '#6b7280' }}>{g.estimatedCalls?.toLocaleString()} calls</span>
                                    </React.Fragment>
                                  ))}
                                </div>
                                <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                                  {estimation.estimatedCost > 0 ? (
                                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#92400e' }}>
                                      Estimated additional API cost: €{estimation.estimatedCost.toFixed(2)}
                                      <div style={{ fontSize: '0.8rem', fontWeight: 400, color: '#78350f', marginTop: '3px' }}>Based on €{estimation.costPer10k} per 10,000 API transactions</div>
                                    </div>
                                  ) : (
                                    <div style={{ fontSize: '0.875rem', fontWeight: 400, color: '#166534' }}>
                                      These {estimation.totalApiCalls?.toLocaleString()} API calls will still be billed as per our OHIP terms.
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Database Connection — DB type vaults only */}
              {wizardStep === SETTINGS_STEP && !isApiExtract && (
                <div className="wizard-panel">
                  <div className="form-section">
                    <div className="vault-cred-header" style={{ marginBottom: '1rem' }}>
                      <div className="vault-cred-title">Database Connection</div>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                        Select an existing database credential or create a new one for this vault.
                      </p>
                    </div>
                    {isViewMode ? (
                      formData.credential_entity_type && formData.credential_entity_type !== 'own' ? (
                        <div style={{ padding: '0.75rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{formData.credential_name || 'Credential'}</span>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', marginLeft: '0.5rem' }}>({formData.credential_entity_type})</span>
                        </div>
                      ) : (
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>No credential selected.</p>
                      )
                    ) : selectedVaultSource?.marketplace_id ? (() => {
                      // Same collapsed-chip / "+ Add Credential" / full-screen picker pattern as the
                      // OHIP (API Settings) step — the shared CredentialSelector.
                      // Persist the credential to the vault immediately on pick/remove (edit mode) so the
                      // DB — and therefore the downloaded client's baked-in config — always reflects the
                      // selected credential_id, without depending on the user advancing the wizard.
                      const persistCred = (credFields) => {
                        if (!id) return;
                        vaultService.updateVault(id, { ...formData, ...credFields }).catch(err => console.error('Failed to persist credential:', err));
                      };
                      const onPick = (cred, values) => {
                        applyVaultOverrides(vaultSourceSettings, values, setVaultSettingValues);
                        const credFields = {
                          credential_entity_type: cred.entity_type,
                          credential_entity_id:   cred.entity_id,
                          credential_id:          cred.credential_id,
                          credential_name:        cred.credential_name || formData.credential_name,
                          use_hconnect_credentials: 0,
                        };
                        setFormData(prev => ({ ...prev, ...credFields }));
                        setEditingCred(false);
                        persistCred(credFields);
                      };
                      const onRemoveCred = () => {
                        const credFields = { credential_entity_type: null, credential_entity_id: null, credential_id: null, credential_name: null, use_hconnect_credentials: 0 };
                        setFormData(prev => ({ ...prev, ...credFields }));
                        persistCred(credFields);
                      };
                      const hasCred = formData.credential_entity_type
                        && !['own', 'hconnect'].includes(formData.credential_entity_type)
                        && formData.credential_id;
                      return (
                        <CredentialSelector
                          marketplaceId={selectedVaultSource.marketplace_id}
                          hotelId={formData.hotelid}
                          accountId={accountId || hotelRecord?.accountid || selectedAccount}
                          partnerId={partnerData?.partnerid}
                          canEdit={isSuperAdmin}
                          title={null}
                          subtitle="Select an existing database credential — or add a new one at the hotel, chain or partner level."
                          allowAddAt={['hotel', 'account', 'partner']}
                          value={hasCred ? {
                            credential_name: formData.credential_name || 'Credential',
                            _entityType: formData.credential_entity_type,
                            credential_id: formData.credential_id,
                          } : null}
                          // Per-hotel fields for a chain/partner DB credential, asked in the picker with Save.
                          overridesInPicker
                          overrides={vaultOverrideValues(vaultSourceSettings, vaultSettingValues)}
                          overridesTitle="For this vault"
                          onChange={(sel, values) => (sel ? onPick(sel, values) : onRemoveCred())}
                          open={editingCred}
                          onOpenChange={setEditingCred}
                        />
                      );
                    })() : (
                      <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                        No credential schema configured for this vault source.
                      </p>
                    )}

                  </div>

                  <div className="form-section" style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>Night Audit Location</h3>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' }}>Directories where night audit files are stored on the database server.</p>
                    {(() => {
                      const paths = formData.night_audit_paths || [];
                      const hotelCounts = paths.reduce((acc, e) => {
                        const h = (e.hotel || '').trim().toLowerCase();
                        if (h) acc[h] = (acc[h] || 0) + 1;
                        return acc;
                      }, {});
                      const isDuplicateHotel = (hotel) => {
                        const h = (hotel || '').trim().toLowerCase();
                        return h && hotelCounts[h] > 1;
                      };
                      return (
                        <>
                          {paths.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', marginBottom: '0.35rem', padding: '0 0.1rem' }}>
                              <span style={{ fontSize: '0.775rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Directory</span>
                              <span style={{ fontSize: '0.775rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Hotel <span style={{ color: '#ef4444' }}>*</span></span>
                              <span />
                            </div>
                          )}
                          {paths.map((entry, idx) => {
                            const dupHotel = isDuplicateHotel(entry.hotel);
                            return (
                              <div key={idx} style={{ marginBottom: '0.5rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.5rem', alignItems: 'center' }}>
                                  <input
                                    type="text"
                                    value={entry.path || ''}
                                    onChange={e => {
                                      const updated = paths.map((p, i) => i === idx ? { ...p, path: e.target.value } : p);
                                      setFormData(prev => ({ ...prev, night_audit_paths: updated }));
                                    }}
                                    disabled={isViewMode}
                                    placeholder="e.g., C:\Opera\NightAudit"
                                    style={{ padding: '0.45rem 0.65rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem' }}
                                  />
                                  <input
                                    type="text"
                                    value={entry.hotel || ''}
                                    onChange={e => {
                                      const updated = paths.map((p, i) => i === idx ? { ...p, hotel: e.target.value } : p);
                                      setFormData(prev => ({ ...prev, night_audit_paths: updated }));
                                    }}
                                    disabled={isViewMode}
                                    placeholder="Hotel name"
                                    style={{ padding: '0.45rem 0.65rem', border: `1px solid ${dupHotel ? '#ef4444' : '#d1d5db'}`, borderRadius: 6, fontSize: '0.875rem' }}
                                  />
                                  {!isViewMode && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = paths.filter((_, i) => i !== idx);
                                        setFormData(prev => ({ ...prev, night_audit_paths: updated }));
                                      }}
                                      style={{ padding: '0.4rem 0.6rem', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, color: '#ef4444', cursor: 'pointer', flexShrink: 0 }}
                                      title="Remove"
                                    >
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                {dupHotel && (
                                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#ef4444' }}>Hotel must be unique within this list.</p>
                                )}
                              </div>
                            );
                          })}
                          {!isViewMode && (
                            <button
                              type="button"
                              className="pp-criteria-add-btn"
                              style={{ marginTop: paths.length > 0 ? '0.5rem' : 0 }}
                              onClick={() => setFormData(prev => ({ ...prev, night_audit_paths: [...(prev.night_audit_paths || []), { path: '', hotel: '' }] }))}
                            >
                              + Add Directory
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Step 2: Data Groups */}
              {wizardStep === GROUP_STEP && (
                <div className="wizard-panel">
                  <div className="form-section">
                    <h3>Select Data Groups to Export</h3>
                    <p style={{ color: '#666', marginBottom: '16px' }}>
                      Choose which data groups this vault will export. At least one group must be selected.
                    </p>
                    {availableGroups.length === 0 ? (
                      <p style={{ color: '#999' }}>No data groups available for this vault source.</p>
                    ) : (
                      <div className="group-selection-list">
                        {availableGroups.map(group => (
                          <label key={group.id} className="group-selection-item" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', marginBottom: '8px', cursor: isViewMode ? 'default' : 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedGroupIds.includes(group.id)}
                              onChange={() => {
                                if (isViewMode) return;
                                setSelectedGroupIds(prev =>
                                  prev.includes(group.id)
                                    ? prev.filter(gid => gid !== group.id)
                                    : [...prev, group.id]
                                );
                              }}
                              disabled={isViewMode}
                            />
                            {group.icon && (
                              <span className="material-icons" style={{ fontSize: '22px', color: '#555' }}>{group.icon}</span>
                            )}
                            <span style={{ fontWeight: 500 }}>{group.name}</span>
                            {group.data_type_key && (
                              <span style={{ fontSize: '0.8em', color: '#888', marginLeft: 'auto' }}>{group.data_type_key}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Payment */}
              {wizardStep === PAYMENT_STEP && (
                <div className="wizard-panel">
                  {(() => {
                    const invoicePending = formData.status === 'payment' && parseInt(formData.invoice_enabled) === 1;
                    const paymentDone = (STATUS_TO_STEP[formData.status] || 1) > PAYMENT_STEP
                      || (formData.status === 'payment' && parseInt(formData.invoice_enabled) !== 1);

                    const summaryHasPayments = (paymentSummary?.subscriptions?.length > 0) || (paymentSummary?.invoices?.length > 0);
                    if (invoicePending || paymentDone || summaryHasPayments) {
                      const effectiveAccountId = accountId || hotelRecord?.accountid || selectedAccount;
                      const subs = paymentSummary?.subscriptions || [];
                      const invs = paymentSummary?.invoices || [];
                      const hasActive = paymentSummary?.hasActivePayment ?? false;
                      const fmtAmt = (amount, currency) =>
                        amount != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'EUR' }).format(amount) : '—';
                      const targetLabels = { hotel: 'Hotel', chain: 'Chain', partner: 'Partner' };
                      return (
                        <div className={`wizard-status-block${paymentDone ? ' complete' : ''}`}>
                          <div className="wizard-status-icon">{invoicePending && !paymentDone ? '🧾' : '✓'}</div>
                          <h3>Payments</h3>

                          {/* Invoice pending notice */}
                          {invoicePending && (
                            <div style={{ margin: '0.75rem 0 1rem', padding: '0.875rem 1.125rem', background: '#fef3c7', border: '1.5px solid #fcd34d', borderRadius: '8px', fontSize: '0.9rem', lineHeight: 1.6 }}>
                              <strong>⏳ Awaiting invoice payment</strong>
                              <p style={{ margin: '0.25rem 0 0' }}>Vault processing will begin once the invoice is paid.</p>
                              {invoiceDetails && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                  <div><strong>Sent to:</strong> {invoiceDetails.email_sent_to || '—'}</div>
                                  <div><strong>Addressed to:</strong> {targetLabels[invoiceDetails.target_type] || invoiceDetails.target_type}</div>
                                  <div><strong>Sent on:</strong> {invoiceDetails.created_at ? new Date(invoiceDetails.created_at).toLocaleString() : '—'}</div>
                                  {invoiceDetails.payment_url && (
                                    <a href={invoiceDetails.payment_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontWeight: 600, display: 'inline-block', marginTop: '0.25rem' }}>View Payment Link</a>
                                  )}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                                <button
                                  type="button"
                                  className="btn-process"
                                  disabled={invoiceResending}
                                  onClick={async () => {
                                    if (!invoiceDetails) return;
                                    setInvoiceResending(true);
                                    try {
                                      await vaultPaymentService.createInvoice({
                                        vaultId: parseInt(id),
                                        vaultName: formData.name,
                                        targetType: invoiceDetails.target_type,
                                        hotelId: formData.hotelid,
                                        accountId: effectiveAccountId,
                                        stripePriceId: invoiceDetails.stripe_price_id || null,
                                        retentionYears: parseRetentionYears(formData.minimum_retention_date),
                                      });
                                      setInvoiceDetails(null);
                                      vaultPaymentService.getInvoice(id).then(r => setInvoiceDetails(r.data.invoice)).catch(() => {});
                                    } catch (err) {
                                      setError('Failed to resend invoice: ' + (err.response?.data?.message || err.message));
                                    } finally {
                                      setInvoiceResending(false);
                                    }
                                  }}
                                >
                                  {invoiceResending ? 'Resending...' : 'Resend Invoice'}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Subscriptions */}
                          {subs.map(sub => (
                            <div key={sub.id} style={{ marginTop: '10px', padding: '12px 14px', background: sub.cancelAtPeriodEnd ? '#fef3c7' : '#f0fdf4', border: `1px solid ${sub.cancelAtPeriodEnd ? '#fcd34d' : '#86efac'}`, borderRadius: '8px', fontSize: '13px', color: '#374151' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                <div>
                                  <strong>{fmtAmt(sub.amount, sub.currency)}{sub.interval ? ` / ${sub.intervalCount > 1 ? `${sub.intervalCount} ` : ''}${sub.interval}` : ''}</strong>
                                  {sub.card && <div style={{ marginTop: '3px' }}>💳 {sub.card.brand} •••• {sub.card.last4} · {sub.card.exp_month}/{sub.card.exp_year}</div>}
                                  {sub.cancelAtPeriodEnd
                                    ? <div style={{ marginTop: '3px', color: '#92400e' }}>Active until {sub.renewalDate || formatPeriodEnd(sub.currentPeriodEnd) || (sub.retentionEnd ? new Date(sub.retentionEnd).toLocaleDateString('en-GB') : '')}</div>
                                    : (sub.renewalDate || formatPeriodEnd(sub.currentPeriodEnd))
                                      ? <div style={{ marginTop: '3px' }}>Renews {sub.renewalDate || formatPeriodEnd(sub.currentPeriodEnd)}</div>
                                      : null
                                  }
                                  {isSuperAdmin && <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{sub.id}</div>}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, background: sub.cancelAtPeriodEnd ? '#fef3c7' : sub.status === 'active' ? '#dcfce7' : '#fee2e2', color: sub.cancelAtPeriodEnd ? '#92400e' : sub.status === 'active' ? '#166534' : '#991b1b', whiteSpace: 'nowrap' }}>
                                    {sub.cancelAtPeriodEnd ? 'Cancels' : sub.status}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    style={{ fontSize: '12px', padding: '4px 10px' }}
                                    onClick={() => setPaymentModal({ hotelId: formData.hotelid, accountId: effectiveAccountId, subscriptionId: sub.id })}
                                  >
                                    Manage
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Invoices from summary (excludes currently-pending invoice shown above) */}
                          {invs.filter(inv => !invoicePending || inv.status !== 'pending').map(inv => (
                            <div key={inv.id} style={{ marginTop: '10px', padding: '12px 14px', background: inv.status === 'paid' ? '#f0fdf4' : '#fafaf9', border: `1px solid ${inv.status === 'paid' ? '#86efac' : '#d1d5db'}`, borderRadius: '8px', fontSize: '13px', color: '#374151' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                <div>
                                  <strong>🧾 Invoice</strong>
                                  {inv.target_type && <span style={{ marginLeft: '6px', color: '#6b7280' }}>— {targetLabels[inv.target_type] || inv.target_type}</span>}
                                  <div style={{ marginTop: '3px', color: '#6b7280' }}>Sent to: {inv.email_sent_to || '—'}</div>
                                  <div style={{ marginTop: '2px', color: '#6b7280' }}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB') : ''}</div>
                                  {inv.retentionEnd && <div style={{ marginTop: '3px', color: '#166534', fontWeight: 600 }}>Paid until: {new Date(inv.retentionEnd).toLocaleDateString('en-GB')}</div>}
                                  {isSuperAdmin && inv.stripe_invoice_id && <div style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{inv.stripe_invoice_id}</div>}
                                  {inv.payment_url && inv.status !== 'paid' && (
                                    <a href={inv.payment_url} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontWeight: 600, fontSize: '12px', marginTop: '4px', display: 'inline-block' }}>View Invoice</a>
                                  )}
                                </div>
                                <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, background: inv.status === 'paid' ? '#dcfce7' : '#fef3c7', color: inv.status === 'paid' ? '#166534' : '#92400e', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  {inv.status}
                                </span>
                              </div>
                            </div>
                          ))}

                          {!paymentSummary && <p style={{ marginTop: '10px', color: '#6b7280', fontSize: '13px' }}>Loading payment details...</p>}

                          {/* No valid payment — vault disabled (only when payments exist but none are active) */}
                          {paymentSummary && summaryHasPayments && !hasActive && (
                            <div style={{ margin: '12px 0', padding: '14px 16px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', fontSize: '13px', color: '#991b1b' }}>
                              <strong>⛔ No active payment — vault search is disabled</strong>
                              <p style={{ margin: '6px 0 0' }}>All subscriptions are cancelled or no valid payment exists. Add a payment below to restore access.</p>
                            </div>
                          )}

                          {/* Past-due warning */}
                          {paymentSummary && subs.some(s => s.status === 'past_due') && (
                            <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '13px', color: '#991b1b' }}>
                              ⚠️ <strong>Payment missed</strong> — one or more subscriptions have a failed payment. Vault search access is restricted until payment is resolved. Please contact your vault administrator.
                            </div>
                          )}

                          {/* Retention expired warning */}
                          {paymentSummary && !hasActive && paymentSummary.latestRetentionEnd && new Date(paymentSummary.latestRetentionEnd) < new Date() && (
                            <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', fontSize: '13px', color: '#991b1b' }}>
                              ⛔ <strong>Vault retention period has ended</strong> — vault search access is suspended. Add a new payment to restore access.
                            </div>
                          )}

                          {/* Retention approaching warning */}
                          {paymentSummary && paymentSummary.latestRetentionEnd && (() => {
                            const end = new Date(paymentSummary.latestRetentionEnd);
                            const daysLeft = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
                            if (daysLeft > 0 && daysLeft <= 30) {
                              return (
                                <div style={{ marginTop: '12px', padding: '10px 14px', background: daysLeft <= 7 ? '#fff7ed' : '#fefce8', border: `1px solid ${daysLeft <= 7 ? '#fb923c' : '#fde047'}`, borderRadius: '8px', fontSize: '13px', color: daysLeft <= 7 ? '#9a3412' : '#713f12' }}>
                                  {daysLeft <= 7 ? '🚨' : '⚠️'} <strong>Vault retention ends in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> — on {end.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. Extend now to avoid disruption.
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Retention dates summary */}
                          {paymentSummary?.latestRetentionEnd && (
                            <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                              {paymentSummary.latestRetentionStart && <span>Retention start: <strong>{new Date(paymentSummary.latestRetentionStart).toLocaleDateString('en-GB')}</strong></span>}
                              <span>Retention end: <strong>{new Date(paymentSummary.latestRetentionEnd).toLocaleDateString('en-GB')}</strong></span>
                            </div>
                          )}

                          {/* Add payment */}
                          <div style={{ marginTop: '14px', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <button
                              type="button"
                              className={hasActive ? 'btn-secondary' : 'btn-process'}
                              onClick={() => {
                                setPendingSubmitData({ formData, isApiExtract, effectiveAccountId });
                                setPaymentModal({ hotelId: formData.hotelid, accountId: effectiveAccountId });
                              }}
                            >
                              Add Payment
                            </button>
                            {isSuperAdmin && !isApiExtract && (
                              <button
                                type="button"
                                className="pp-criteria-add-btn sa-only"
                                disabled={loading}
                                onClick={async () => {
                                  setLoading(true);
                                  setError(null);
                                  try {
                                    await vaultService.updateVault(id, { ...formData, status: 'client_generated', use_free_seat: 1, invoice_enabled: 0 });
                                    setFormData(prev => ({ ...prev, status: 'client_generated', use_free_seat: 1, invoice_enabled: 0 }));
                                    setWizardStep(DOWNLOAD_STEP);
                                  } catch (err) {
                                    setError('Failed to mark as paid: ' + (err.response?.data?.message || err.message));
                                  } finally {
                                    setLoading(false);
                                  }
                                }}
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                  <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Mark as Paid
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="wizard-status-block">
                        <div className="wizard-status-icon">💳</div>
                        <h3>Payment Required</h3>
                        <p>A payment is required to proceed with vault setup.</p>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            type="button"
                            className="btn-process"
                            onClick={() => {
                              const effectiveAccountId = accountId || hotelRecord?.accountid || selectedAccount;
                              setPendingSubmitData({ formData, isApiExtract, effectiveAccountId });
                              setPaymentModal({ hotelId: formData.hotelid, accountId: effectiveAccountId });
                            }}
                          >
                            Make Payment
                          </button>
                          {isSuperAdmin && !isApiExtract && (
                            <button
                              type="button"
                              className="pp-criteria-add-btn sa-only"
                              disabled={loading}
                              onClick={async () => {
                                setLoading(true);
                                setError(null);
                                try {
                                  await vaultService.updateVault(id, { ...formData, status: 'client_generated', use_free_seat: 1, invoice_enabled: 0, wizard_step: DOWNLOAD_STEP });
                                  setFormData(prev => ({ ...prev, status: 'client_generated', use_free_seat: 1, invoice_enabled: 0, wizard_step: DOWNLOAD_STEP }));
                                  setWizardStep(DOWNLOAD_STEP);
                                } catch (err) {
                                  setError('Failed to mark as paid: ' + (err.response?.data?.message || err.message));
                                } finally {
                                  setLoading(false);
                                }
                              }}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Mark as Paid
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                </div>
              )}

              {/* Step Download App (non-API / OperaV5 only) */}
              {!isApiExtract && wizardStep === DOWNLOAD_STEP && (
                <div className="wizard-panel">
                  <div className="form-section">
                    {formData.status === 'client_executed' ? (
                      <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        ✓ App connected — extraction is running. Click <strong>Next</strong> to continue.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, marginBottom: '1rem', fontSize: '0.825rem', color: '#92400e' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        Download and run the app below. The <strong>Next</strong> button will enable automatically once the app connects.
                      </div>
                    )}
                    <div style={{ marginBottom: '1.5rem', padding: '1.25rem 1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 10, color: '#fff', display: 'flex', gap: '1rem', alignItems: 'flex-start', boxShadow: '0 4px 14px rgba(102,126,234,0.35)' }}>
                      <div style={{ fontSize: '2rem', lineHeight: 1, flexShrink: 0 }}>🔐</div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.35rem' }}>Your personalised Vault app is being compiled</div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.9, lineHeight: 1.6 }}>
                          We embed your database credentials, encrypted, directly into the app. Once you download and run it, the app will securely connect to your database and extract your data into Vault — no manual configuration needed.
                        </div>
                      </div>
                    </div>
                    {generateSuccess && <div className="success-message">{generateSuccess}</div>}
                    <div className="generate-client-section">
                      <label htmlFor="platform" style={{ display: 'block', fontSize: '0.825rem', fontWeight: 600, color: '#374151', marginBottom: '0.4rem' }}>Select Platform:</label>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <select id="platform" value={selectedPlatform} onChange={(e) => setSelectedPlatform(e.target.value)} disabled={generating} className="platform-select" style={{ flex: 1 }}>
                          {Object.entries(PLATFORMS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                        <button type="button" onClick={(e) => handleGenerateBinary(e)} disabled={generating} className="pp-rules-add-btn" style={{ flexShrink: 0, opacity: generating ? 0.6 : 1, cursor: generating ? 'not-allowed' : 'pointer' }}>
                          {generating ? (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="pp-preview-spinner" style={{ flexShrink: 0 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.3"/>
                              <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </svg>
                          ) : (
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="7 10 12 15 17 10"/>
                              <line x1="12" y1="15" x2="12" y2="3"/>
                            </svg>
                          )}
                          {generating ? 'Generating…' : 'Generate'}
                        </button>
                        <button type="button" onClick={() => { const vaultId = parseInt(formData.id); if (vaultId && !isNaN(vaultId)) fetchDownloads(vaultId); }} className="pp-refresh-btn" style={{ flexShrink: 0 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <polyline points="1 4 1 10 7 10"/>
                            <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                          </svg>
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="downloads-section">
                      {generatingDownloadId && (
                        <div className="progress-container">
                          <div className="progress-header">
                            <span className="progress-title">Generating App...</span>
                            <span className="progress-percentage">{Math.round(progress)}%</span>
                          </div>
                          <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${progress}%` }}>
                              {progress > 10 && <span className="progress-bar-text">{Math.round(progress)}%</span>}
                            </div>
                          </div>
                          <div className="progress-message">This process typically takes 2-3 minutes. Please wait...</div>
                        </div>
                      )}
                      <div className="downloads-header"><h4>Available Downloads</h4></div>
                      {downloads.length > 0 ? (
                        <>
                          <div className="downloads-table-container">
                            <table className="downloads-table">
                              <thead>
                                <tr>
                                  <th>Name</th><th>Status</th><th>Created</th><th>Expires In</th><th className="actions-column">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {downloads.map(download => (
                                  <tr key={download.id}>
                                    <td>{download.name}</td>
                                    <td>
                                      {download.status === 'completed' && <span className="download-status status-completed">✓ Ready</span>}
                                      {download.status === 'downloaded' && <span className="download-status status-downloaded">✓ Downloaded</span>}
                                      {download.status === 'pending' && <span className="download-status status-pending">⏳ Pending</span>}
                                      {download.status === 'generating' && <span className="download-status status-generating">⚙️ Generating</span>}
                                      {download.status === 'failed' && <span className="download-status status-failed">✗ Failed</span>}
                                    </td>
                                    <td>{formatDate(download.created_at)}</td>
                                    <td>{(() => { const t = getTimeRemaining(download.created_at); return <span className={`expiry-badge ${t.expired ? 'expired' : t.hours < 2 ? 'expiring-soon' : 'expires-later'}`}>{t.expired ? 'Expired' : t.text}</span>; })()}</td>
                                    <td className="actions-column" style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                                      {download.status === 'completed' && download.s3url && (() => { const t = getTimeRemaining(download.created_at); return (
                                        <button type="button" onClick={() => !t.expired && handleDownloadFile(download.id)} disabled={t.expired} className="pp-rules-add-btn">
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                          Download
                                        </button>
                                      ); })()}
                                      {download.status === 'downloaded' && (() => {
                                        const platform = platformFromDownloadName(download.name);
                                        return (
                                          <button type="button" onClick={async (e) => { if (platform) { setSelectedPlatform(platform); } await handleGenerateBinary(e); }} disabled={generating} className="pp-rules-add-btn">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/></svg>
                                            Regenerate
                                          </button>
                                        );
                                      })()}
                                      <button type="button" onClick={() => handleDeleteDownload(download.id, download.name)} className="btn-delete-download">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="downloads-summary"><span>Total Downloads: {downloads.length}</span></div>
                        </>
                      ) : (
                        <div className="no-downloads"><p>No downloads available yet. Generate an app to get started.</p></div>
                      )}
                    </div>
                    {(() => {
                      const instructions = PLATFORM_INSTRUCTIONS[selectedPlatform];
                      const completed = downloads.find(d => d.status === 'completed' && d.s3url && d.s3url.includes(selectedPlatform));
                      const filename = (() => {
                        if (!completed?.s3url) return selectedPlatform;
                        try {
                          return new URL(completed.s3url).pathname.split('/').pop();
                        } catch {
                          return completed.s3url.split('/').pop().split('?')[0];
                        }
                      })();
                      return (
                        <div style={{ marginTop: '1.25rem', padding: '1rem 1.25rem', background: '#f0f4ff', border: '1px solid #c7d4f7', borderRadius: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#333', marginBottom: '0.6rem' }}>{instructions.icon} How to run on {PLATFORMS[selectedPlatform]}</div>
                          <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {instructions.steps.map((step, i) => (
                              <li key={i} style={{ marginBottom: '0.4rem', color: '#444', fontSize: '0.825rem', lineHeight: 1.6 }}>
                                {typeof step === 'function' ? step(filename) : step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Step Records Extracted */}
              {wizardStep === EXTRACT_STEP && (
                <div className="wizard-panel">
                  {isApiExtract ? (
                    <div className="form-section">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        {(isSuperAdmin || formData.status !== 'vault_ready') && (
                          <button
                            type="button"
                            className="btn-process"
                            onClick={() => openOhipExtractModal(null)}
                            disabled={processing}
                          >
                            {processing ? 'Starting...' : 'Extract Records'}
                          </button>
                        )}
                        {processResult && !processResult.loading && (
                          <span className={`process-result ${processResult.success ? 'success' : 'error'}`}>{processResult.message}</span>
                        )}
                      </div>
                      {isSuperAdmin && derivedS3Location && (
                        <div className="sa-only-panel" style={{ marginTop: '1rem', padding: '0.5rem 0.875rem', borderRadius: 7, fontSize: '0.825rem', color: '#374151' }}>
                          <span style={{ fontWeight: 600, color: '#6b21a8' }}>Expected S3 location: </span>
                          <code style={{ fontSize: '0.8rem', color: '#1e293b' }}>s3://{derivedS3Location.bucket}/{derivedS3Location.prefix ?? '(prefix not yet assigned — generate the app first)'}</code>
                        </div>
                      )}
                      <div style={{ marginTop: '1.25rem' }}>{renderFileBrowser()}</div>
                    </div>
                  ) : (
                    <div className="form-section">
                      {isSuperAdmin && derivedS3Location && (
                        <div className="sa-only-panel" style={{ marginBottom: '0.875rem', padding: '0.5rem 0.875rem', borderRadius: 7, fontSize: '0.825rem', color: '#374151' }}>
                          <span style={{ fontWeight: 600, color: '#6b21a8' }}>Expected S3 location: </span>
                          <code style={{ fontSize: '0.8rem', color: '#1e293b' }}>s3://{derivedS3Location.bucket}/{derivedS3Location.prefix ?? '(prefix not yet assigned — generate the app first)'}</code>
                        </div>
                      )}
                      {renderFileBrowser()}
                    </div>
                  )}
                </div>
              )}

              {/* Step PROCESSED_STEP: Records Processed */}
              {isApiExtract && wizardStep === PROCESSED_STEP && (
                <div className="wizard-panel">
                  {(STATUS_TO_STEP[formData.status] || 1) >= PROCESSED_STEP ? (
                    <div className="wizard-status-block complete">
                      <div className="wizard-status-icon">✓</div>
                      <h3>Records Processed</h3>
                      <p>All records have been successfully processed.</p>
                      {formData.records_summary && isSuperAdmin && (
                        <div className="records-summary" style={{ marginTop: '1rem' }}>
                          <h4>Records Summary</h4>
                          <table className="summary-table">
                            <thead><tr><th>Table</th><th>Record Count</th></tr></thead>
                            <tbody>
                              {[
                                { label: 'Folios', value: formData.records_summary.folios },
                                { label: 'Folio History', value: formData.records_summary.folioHistory },
                                { label: 'Reservations', value: formData.records_summary.reservations },
                                { label: 'E-Reg Cards', value: formData.records_summary.eregCards }
                              ].map(row => (
                                <tr key={row.label}><td>{row.label}</td><td>{row.value != null ? row.value.toLocaleString() : '—'}</td></tr>
                              ))}
                            </tbody>
                          </table>
                          {formData.records_summary.s3Path && <div className="summary-meta"><span className="summary-label">Processed Location:</span><code>{formData.records_summary.s3Path.replace(/^s3:\/\//, '')}</code></div>}
                          {formData.records_summary.athenaDatabase && <div className="summary-meta"><span className="summary-label">Database:</span><code>{formData.records_summary.athenaDatabase}</code></div>}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="wizard-status-block">
                      <div className="wizard-status-icon">⏳</div>
                      <h3>Processing Records</h3>
                      <p>Records are being extracted and processed. This may take several minutes.</p>
                    </div>
                  )}
                  {processResult && !processResult.loading && (
                    <span className={`process-result ${processResult.success ? 'success' : 'error'}`}>{processResult.message}</span>
                  )}
                </div>
              )}

              {/* Step TABLES_STEP: Vault System Tables Created */}
              {isApiExtract && wizardStep === TABLES_STEP && (
                <div className="wizard-panel">
                  {(STATUS_TO_STEP[formData.status] || 1) >= TABLES_STEP ? (
                    <div className="wizard-status-block complete">
                      <div className="wizard-status-icon">✓</div>
                      <h3>Vault System Tables Created</h3>
                      <p>The vault system tables have been successfully created.</p>
                    </div>
                  ) : (
                    <div className="wizard-status-block">
                      <div className="wizard-status-icon">⏳</div>
                      <h3>Creating Vault Tables</h3>
                      <p>Vault system tables are being created. Please wait...</p>
                    </div>
                  )}
                </div>
              )}

              {/* Step READY_STEP: Settings panel — admin controls + wizard navigation */}
              {wizardStep === READY_STEP && (() => {
                const summaryHasPayments = (paymentSummary?.subscriptions?.length > 0) || (paymentSummary?.invoices?.length > 0);
                // Superadmins are never payment-blocked — they need to access / reprocess vaults for support.
                const accessBlocked = !isSuperAdmin && (formData.status !== 'vault_ready' || (summaryHasPayments && !paymentSummary?.hasActivePayment));
                const statusMessages = {
                  payment_missed: { title: '⛔ Payment missed', body: 'A subscription payment has failed. Please update your payment method to restore access.' },
                  subscription_cancelled: { title: '⛔ Subscription cancelled', body: 'Your subscription has been cancelled. Please set up a new payment to restore access.' },
                  retention_expired: { title: '⛔ Retention period expired', body: 'The retention period has ended. Please set up a new payment to extend your retention period.' },
                };
                const blockedMsg = statusMessages[formData.status] || { title: '⛔ No active payment', body: 'All subscriptions are cancelled or no valid payment is on record.' };
                return (
                <div className="wizard-panel">
                  {/* Payment access warning */}
                  {accessBlocked && (
                    <div style={{ marginBottom: '1.5rem', padding: '16px 18px', background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '13px' }}>
                      <strong style={{ fontSize: '14px' }}>{blockedMsg.title}</strong>
                      <p style={{ margin: '8px 0 12px' }}>{blockedMsg.body}</p>
                      <button type="button" className="btn-process" onClick={() => setWizardStep(PAYMENT_STEP)}>
                        Go to Payment
                      </button>
                    </div>
                  )}
                  {isSuperAdmin && (formData.status === 'vault_ready' || formData.status === 'error') && (
                    <div className="vault-process-actions" style={{ marginBottom: '1rem' }}>
                      <button type="button" className="btn-reprocess"
                        onClick={() => isApiExtract ? openOhipExtractModal('full') : handleProcessVault(true)}
                        disabled={processing}>
                        {processing ? 'Processing...' : 'Reprocess Vault'}
                      </button>
                      <button type="button" className="btn-reprocess-skip"
                        onClick={() => isApiExtract ? openOhipExtractModal('skipExtraction') : (async () => {
                          setProcessing(true);
                          setProcessResult({ loading: true });
                          try {
                            const result = await v2VaultProcessService.reprocessSkipExtraction(id);
                            setProcessResult({ success: result.success, message: result.message });
                            if (result.success) setPollingActive(true);
                          } catch (err) {
                            setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
                          } finally {
                            setProcessing(false);
                          }
                        })()}
                        disabled={processing}>
                        {processing ? 'Processing...' : 'RE-Process (skipping extraction)'}
                      </button>
                      <button type="button" className="btn-reprocess-skip"
                        onClick={() => isApiExtract ? openOhipExtractModal('pdfsOnly') : (async () => {
                          setProcessing(true);
                          setProcessResult({ loading: true });
                          try {
                            const result = await v2VaultProcessService.reprocessPdfsOnly(id);
                            setProcessResult({ success: result.success, message: result.message });
                          } catch (err) {
                            setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
                          } finally {
                            setProcessing(false);
                          }
                        })()}
                        disabled={processing}>
                        {processing ? 'Processing...' : 'Reprocess PDFs'}
                      </button>
                      {/* Rebuild the materialised_view_* tables only. Shown for OperaV5 (database_extract)
                          vaults because they are the ones with derived tables — an OHIP vault's folios is
                          a real extracted table, so there is nothing here to rebuild.

                          Separate from the reprocess buttons on purpose: those re-run the pipeline over
                          the uploaded files (40+ minutes), this runs one Athena CTAS over data already in
                          the processed bucket (minutes). Before it existed, a derived table that was
                          missing — because the definition changed, or the build failed, or the client was
                          on an older lambda — could only be fixed by a full re-extract. */}
                      {!isApiExtract && (
                        <button type="button" className="btn-reprocess-skip sa-only"
                          title="Rebuild the folio search tables from the data already extracted. No files are re-uploaded or re-extracted."
                          onClick={() => (async () => {
                            setProcessing(true);
                            setProcessResult({ loading: true });
                            try {
                              const result = await v2VaultProcessService.rebuildDerivedTables(id);
                              setProcessResult({ success: result.success, message: result.message });
                            } catch (err) {
                              setProcessResult({ success: false, message: err.response?.data?.message || 'An error occurred' });
                            } finally {
                              setProcessing(false);
                            }
                          })()}
                          disabled={processing}>
                          {processing ? 'Processing...' : 'Rebuild derived tables'}
                        </button>
                      )}
                      {processResult && !processResult.loading && (
                        <span className={`process-result ${processResult.success ? 'success' : 'error'}`}>{processResult.message}</span>
                      )}
                    </div>
                  )}
                  {/* Reconciliation — shown to all roles when vault_ready and OHIP (api_extract) */}
                  {formData.status === 'vault_ready' && isApiExtract && (() => {
                    const rec = reconciliation;
                    const fmtNum = (n) => n != null ? n.toLocaleString() : '—';
                    const mismatch = (a, b) => a != null && b != null && a !== b;
                    const handleRecalculate = async () => {
                      setReconcileLoading(true);
                      try {
                        const res = await vaultReconcileService.recalculate(parseInt(id));
                        setReconciliation(res.data?.reconciliation || null);
                      } catch (err) {
                        console.error('Recalculate failed:', err.message);
                      } finally {
                        setReconcileLoading(false);
                      }
                    };
                    const handleDownload = () => {
                      if (!rec) return;
                      const rows = [
                        ['Metric', 'Extracted from OHIP', 'Loaded to Athena'],
                        ['Reservations', rec.reservations_extracted, rec.reservations_in_athena],
                        ['Folios', rec.folios_extracted, rec.folios_in_athena],
                        ['Folio History', rec.folio_history_extracted, rec.folio_history_in_athena],
                        ['E-Reg Cards', rec.eregcards_extracted, rec.eregcards_in_athena],
                        ['Folio PDFs', rec.folio_pdfs_extracted, ''],
                        ['', '', ''],
                        ['Integrity Checks', '', ''],
                        ['Reservations without folio', rec.reservations_without_folio, ''],
                        ['Folios without reservation', rec.folios_without_reservation, ''],
                      ];
                      if (rec.notes) rows.push(['', '', ''], ['Notes', rec.notes, '']);
                      const csv = rows.map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `vault-${id}-reconciliation.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    };
                    return (
                      <div style={{ marginBottom: '1.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>Data Reconciliation</h4>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {rec && (
                              <button type="button" onClick={handleDownload}
                                style={{ fontSize: '12px', padding: '4px 12px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', color: '#334155' }}>
                                Download CSV
                              </button>
                            )}
                            {isSuperAdmin && (
                              <button type="button" onClick={handleRecalculate} disabled={reconcileLoading}
                                style={{ fontSize: '12px', padding: '4px 12px', background: '#1e40af', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff' }}>
                                {reconcileLoading ? 'Recalculating...' : 'Recalculate'}
                              </button>
                            )}
                          </div>
                        </div>
                        {!rec ? (
                          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>No reconciliation data available.</p>
                        ) : (
                          <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ background: '#e2e8f0', textAlign: 'left' }}>
                                  <th style={{ padding: '6px 10px', fontWeight: 600 }}>Record Type</th>
                                  <th style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right' }}>Extracted from OHIP</th>
                                  <th style={{ padding: '6px 10px', fontWeight: 600, textAlign: 'right' }}>Loaded to Athena</th>
                                </tr>
                              </thead>
                              <tbody>
                                {[
                                  { label: 'Reservations', ext: rec.reservations_extracted, ath: rec.reservations_in_athena },
                                  { label: 'Folios', ext: rec.folios_extracted, ath: rec.folios_in_athena },
                                  { label: 'Folio History', ext: rec.folio_history_extracted, ath: rec.folio_history_in_athena },
                                  { label: 'E-Reg Cards', ext: rec.eregcards_extracted, ath: rec.eregcards_in_athena },
                                  { label: 'Folio PDFs', ext: rec.folio_pdfs_extracted, ath: null },
                                ].map(({ label, ext, ath }) => (
                                  <tr key={label} style={{ borderBottom: '1px solid #e2e8f0', background: mismatch(ext, ath) ? '#fef2f2' : 'transparent' }}>
                                    <td style={{ padding: '6px 10px', color: '#374151' }}>{label}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#374151' }}>{fmtNum(ext)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', color: mismatch(ext, ath) ? '#dc2626' : '#374151', fontWeight: mismatch(ext, ath) ? 600 : 400 }}>
                                      {ath != null ? fmtNum(ath) : '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div style={{ marginTop: '12px', padding: '10px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Integrity Checks</div>
                              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                <div>
                                  <span style={{ fontSize: '13px', color: '#374151' }}>Reservations without folio: </span>
                                  <span style={{ fontSize: '13px', fontWeight: 600, color: rec.reservations_without_folio > 0 ? '#d97706' : '#16a34a' }}>{fmtNum(rec.reservations_without_folio)}</span>
                                </div>
                                <div>
                                  <span style={{ fontSize: '13px', color: '#374151' }}>Folios without reservation: </span>
                                  <span style={{ fontSize: '13px', fontWeight: 600, color: rec.folios_without_reservation > 0 ? '#d97706' : '#16a34a' }}>{fmtNum(rec.folios_without_reservation)}</span>
                                </div>
                              </div>
                              {rec.notes && (
                                <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626' }}><strong>Notes:</strong> {rec.notes}</div>
                              )}
                            </div>
                            {rec.calculated_at && (
                              <div style={{ marginTop: '8px', fontSize: '11px', color: '#94a3b8', textAlign: 'right' }}>
                                Last calculated: {new Date(rec.calculated_at).toLocaleString('en-GB')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Rev share summary — visible to superadmin and partner users when vault is ready */}
                  {formData.status === 'vault_ready' && (isSuperAdmin || isPartner) && revShareSummary && (() => {
                    const { partner, activeCount, tier, tierSource } = revShareSummary;
                    return (
                      <div style={{ marginBottom: '1.5rem', padding: '14px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px' }}>
                        <div style={{ fontWeight: 600, color: '#15803d', marginBottom: '8px' }}>Revenue Share</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', color: '#374151' }}>
                          <div>
                            <span style={{ color: '#6b7280' }}>Partner: </span>
                            <strong>{partner.companyname}</strong>
                          </div>
                          <div>
                            <span style={{ color: '#6b7280' }}>Active vaults: </span>
                            <strong>{activeCount}</strong>
                          </div>
                          {tier ? (
                            <>
                              <div>
                                <span style={{ color: '#6b7280' }}>Current tier: </span>
                                <strong>{tier.vaults_from}–{tier.vaults_to} vaults</strong>
                              </div>
                              <div>
                                <span style={{ color: '#6b7280' }}>Revenue share: </span>
                                <strong style={{ color: '#15803d' }}>{tier.share_pct}%</strong>
                                <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '4px' }}>({tierSource})</span>
                              </div>
                            </>
                          ) : (
                            <div style={{ color: '#d97706' }}>No matching rev share tier configured</div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Processing message for non-ready vaults (shouldn't normally be seen here, but guard) */}
                  {formData.status !== 'vault_ready' && formData.status !== 'error' && (
                    <div className="wizard-status-block">
                      <div className="wizard-status-icon">⏳</div>
                      <h3>Vault Processing</h3>
                      <p>Extraction and processing is underway.</p>
                    </div>
                  )}

                </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Wizard Navigation + Form Actions */}
        <div className="form-actions wizard-form-actions">
          {/* Previous — view/edit only */}
          {(isViewMode || isEditMode) && (
            <button
              type="button"
              className="btn-wizard-nav btn-wizard-prev"
              onClick={() => setWizardStep(s => Math.max(1, s - 1))}
              disabled={wizardStep === 1}
            >
              ← Previous
            </button>
          )}

          {/* Centre actions */}
          {isViewMode && (
            <>
              {/* In settings mode, show Close Settings instead of normal view buttons */}
              {settingsOpen ? (
                <>
                  {isSuperAdmin && (
                    <button type="button" onClick={handleDelete} className="btn-danger">Delete Vault</button>
                  )}
                  <button type="button" onClick={() => { setSettingsOpen(false); setWizardStep(READY_STEP); }}>
                    ← Close Settings
                  </button>
                </>
              ) : (
                <>
                  {isSuperAdmin && (
                    <button type="button" onClick={() => navigate(`/vault/${id}/edit`)}>Edit Vault</button>
                  )}
                  {isSuperAdmin && (
                    <button type="button" onClick={handleDelete} className="btn-danger">Delete Vault</button>
                  )}
                  <button type="button" onClick={handleCancel}>Back to Hotel</button>
                </>
              )}
            </>
          )}
          {isEditMode && (
            <>
              {settingsOpen ? (
                <>
                  {isSuperAdmin && (
                    <button type="button" onClick={handleDelete} className="btn-danger">Delete Vault</button>
                  )}
                  <button type="button" onClick={() => { setSettingsOpen(false); setWizardStep(READY_STEP); }}>
                    ← Close Settings
                  </button>
                </>
              ) : (
                <>
                  {isSuperAdmin && (
                    <button type="button" onClick={handleDelete} className="btn-danger" disabled={loading}>
                      Delete Vault
                    </button>
                  )}
                </>
              )}
            </>
          )}
          {isCreateMode && (
            <>
              <button type="button" onClick={handleCancel} disabled={loading}>
                Cancel
              </button>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save & Continue'}
              </button>
            </>
          )}

          {/* Next — view/edit only */}
          {(isViewMode || isEditMode) && (
            <button
              type="button"
              className="btn-wizard-nav btn-wizard-next"
              onClick={async () => {
                if (wizardStep === 1) {
                  if (!formData.vaultsourceid || !formData.minimum_retention_date || !formData.extraction_start_date || !formData.extraction_end_date) return;
                }
                if (wizardStep === SETTINGS_STEP && isApiExtract) {
                  if (!formData.credential_entity_type) {
                    setError('Please select a credential to continue.');
                    return;
                  }
                  const credType      = formData.credential_entity_type;
                  const usingOwnCreds = credType === 'own';
                  const needsOverride = credType === 'account' || credType === 'partner';
                  const requiredSettings = usingOwnCreds
                    ? vaultSourceSettings.filter(s => s.required_yn === 'Y' || s.required_yn === 1)
                    : (needsOverride ? vaultSourceSettings.filter(s => s.setting_key === 'HOTEL_ID') : []);
                  const missingRequired = requiredSettings.some(s => !vaultSettingValues[s.id]);
                  if (missingRequired) {
                    setError(usingOwnCreds ? 'Please fill in all required API settings before continuing.' : 'Hotel ID is required.');
                    return;
                  }
                  if (!ohipTestResult?.success || settingsChangedSinceTest) {
                    if (isSuperAdmin) {
                      const warn = settingsChangedSinceTest
                        ? 'Settings have changed since the last connection test. The connection has not been re-tested. Proceed anyway?'
                        : 'The OHIP connection test has not passed. Proceeding may result in extraction failures. Continue anyway?';
                      setConfirmModal({ message: warn, onConfirm: advanceWizardStep });
                      return;
                    } else {
                      setError('Please test the OHIP connection successfully before continuing.');
                      return;
                    }
                  }
                }
                await advanceWizardStep();
              }}
              disabled={loading || wizardStep === READY_STEP || (wizardStep === 1 && (
                !formData.vaultsourceid ||
                !formData.minimum_retention_date ||
                !formData.extraction_start_date ||
                !formData.extraction_end_date
              )) || (wizardStep === GROUP_STEP && selectedGroupIds.length === 0)
              || (wizardStep === SETTINGS_STEP && isApiExtract && (() => {
                if (!formData.credential_entity_type) return true;
                const credType      = formData.credential_entity_type;
                const usingOwnCreds = credType === 'own';
                const needsOverride = credType === 'account' || credType === 'partner';
                const requiredSettings = usingOwnCreds
                  ? vaultSourceSettings.filter(s => s.required_yn === 'Y' || s.required_yn === 1)
                  : (needsOverride ? vaultSourceSettings.filter(s => s.setting_key === 'HOTEL_ID') : []);
                const missingRequired = requiredSettings.some(s => !vaultSettingValues[s.id]);
                return missingRequired || (!isSuperAdmin && (!ohipTestResult?.success || settingsChangedSinceTest));
              })())
              || (isEditMode && wizardStep === SETTINGS_STEP && !isApiExtract && !formData.credential_entity_type)
              || (wizardStep === PAYMENT_STEP && (STATUS_TO_STEP[formData.status] || 1) <= PAYMENT_STEP)
              || (wizardStep === DOWNLOAD_STEP && (STATUS_TO_STEP[formData.status] || 1) <= DOWNLOAD_STEP)
              // Can't advance past extraction / processing / table-creation until the backend status
              // confirms each is complete — otherwise a user could cancel extraction and skip to search.
              || (wizardStep === EXTRACT_STEP   && (STATUS_TO_STEP[formData.status] || 1) <= EXTRACT_STEP)
              || (wizardStep === PROCESSED_STEP && (STATUS_TO_STEP[formData.status] || 1) <= PROCESSED_STEP)
              || (wizardStep === TABLES_STEP    && (STATUS_TO_STEP[formData.status] || 1) <= TABLES_STEP)}
            >
              Next →
            </button>
          )}
        </div>
      </form>

      </>
      /* end wizard layout */
      )}

      {/* Canonical Template Modal */}
      {templateModal && (
        <div className="template-modal-overlay" onClick={() => setTemplateModal(null)}>
          <div className="template-modal" onClick={e => e.stopPropagation()}>

            {/* Step: no templates found */}
            {templateModal.step === 'empty' && (
              <>
                <div className="template-modal-header">
                  <h3 className="template-modal-title">No Templates Found</h3>
                  <button className="template-modal-close-btn" onClick={() => setTemplateModal(null)}>✕ Close</button>
                </div>
                <div className="template-modal-body">
                  <p style={{ color: '#6b7280' }}>No canonical templates are linked to the <strong>{selectedGroup?.name}</strong> source group. Create a canonical group with the same name and add templates to it.</p>
                </div>
              </>
            )}

            {/* Step: pick a template */}
            {templateModal.step === 'pick' && (
              <>
                <div className="template-modal-header">
                  <h3 className="template-modal-title">Select a Template</h3>
                  <button className="template-modal-close-btn" onClick={() => setTemplateModal(null)}>✕ Close</button>
                </div>
                <div className="template-modal-body">
                  <div className="template-picker-list">
                    {templateModal.templates.map(t => (
                      <button
                        key={t.id}
                        className="template-picker-item"
                        onClick={() => handleTemplateSelect(t, templateModal.row)}
                      >
                        <span className="template-picker-name">{t.name}</span>
                        <span className="template-picker-arrow">→</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Step: render template with data */}
            {templateModal.step === 'render' && (
              <>
                <div className="template-modal-header">
                  <h3 className="template-modal-title">{templateModal.name}</h3>
                  <div className="template-modal-actions">
                    {/* Same openPrintWindow the folio detail modal uses, so a folio printed from here
                        and one printed from there are the same document. */}
                    <button
                      className="template-modal-print-btn"
                      onClick={() => openPrintWindow({
                        name: templateModal.name,
                        html: templateModal.html,
                        header: templateModal.header || '',
                        footer: templateModal.footer || '',
                      })}
                    >
                      Print / Save PDF
                    </button>
                    {accountData?.vaultEmailEnabled !== 'N' && (
                      <button
                        className="template-modal-email-btn"
                        onClick={openEmailModal}
                      >
                        ✉ Email
                      </button>
                    )}
                    <button className="template-modal-close-btn" onClick={() => setTemplateModal(null)}>
                      ✕ Close
                    </button>
                  </div>
                </div>
                {templateModal.row && (
                  <div className="template-fields-panel">
                    <button
                      className="template-fields-toggle"
                      onClick={() => setTemplateFieldsOpen(o => !o)}
                    >
                      {templateFieldsOpen ? '▾' : '▸'} Available fields ({Object.keys(templateModal.row).length})
                    </button>
                    {templateFieldsOpen && (
                      <div className="template-fields-grid">
                        {Object.entries(templateModal.row).map(([k, v]) => (
                          <div key={k} className="template-field-row">
                            <code className="template-field-key">{'{{' + k + '}}'}</code>
                            <span className="template-field-val">{v !== null ? String(v) : <em>null</em>}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <iframe
                  className="template-modal-body"
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;}</style></head><body>${templateModal.header || ''}${templateModal.html}${templateModal.footer || ''}</body></html>`}
                  title="Template Preview"
                  sandbox="allow-scripts allow-same-origin"
                />
              </>
            )}

          </div>
        </div>
      )}
      {payloadModal && (
        <div className="payload-modal-overlay" onClick={() => setPayloadModal(null)}>
          <div className="payload-modal" onClick={e => e.stopPropagation()}>
            <div className="payload-modal-header">
              <span className="payload-modal-title">Payload</span>
              <button className="payload-modal-close" onClick={() => setPayloadModal(null)}>✕ Close</button>
            </div>
            {payloadModal.error && <div className="pdf-modal-error">{payloadModal.error}</div>}
            <pre className="payload-modal-body">
              {payloadModal.loading
                ? 'Building the canonical payload…'
                : (typeof payloadModal.json === 'string'
                    ? payloadModal.json
                    : JSON.stringify(payloadModal.json, null, 2))}
            </pre>
          </div>
        </div>
      )}

      {/* Folio PDF Modal */}
      {pdfModal && (
        <div className="pdf-modal-overlay" onClick={() => setPdfModal(null)}>
          <div className="pdf-modal" onClick={e => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <span className="pdf-modal-title">{pdfModal.title || 'Folio'}</span>
              <button className="pdf-modal-close-btn" onClick={() => setPdfModal(null)}>✕ Close</button>
            </div>
            <div className="pdf-modal-body">
              {pdfModal.row && (
                <div className="pdf-modal-fields">
                  {folioDetailFields(pdfModal.row, { operaV5: !isApiExtract, numbers: pdfModal.rendered?.numbers })
                    .map(([k, label, v]) => (
                      <div key={k} className="pdf-modal-field-row">
                        <span className="pdf-modal-field-key" title={k}>{label}</span>
                        <span className="pdf-modal-field-val">{v != null ? String(v) : <em>—</em>}</span>
                      </div>
                    ))}
                </div>
              )}
              {pdfModal.loading && <div className="pdf-modal-info">Checking PDF availability…</div>}
              {pdfModal.error && <div className="pdf-modal-error">{pdfModal.error}</div>}
              {folioPdfError && <div className="pdf-modal-error">{folioPdfError}</div>}
            </div>
            {/* NO STORED PDF: PRINT THE TEMPLATE INSTEAD OF SENDING THE USER AWAY.
                An OperaV5 vault has no folio PDF in S3, but it does have the folio template — so the
                same button renders it and opens the print dialog, which is where a PDF is saved from.
                Telling somebody to "use the folio template" when the screen can do it for them is not
                an answer. Only a group with no template configured has nothing to print. */}
            {/* Same two buttons as OHIP, in the same places, enabling when the document is ready — for
                OHIP that is the S3 availability check, here it is the template render. The user does not
                need to know which vault type they are looking at. */}
            {pdfModal.hasDocument === false && (
              <div className="pdf-modal-footer">
                <button
                  className="pdf-modal-print-btn"
                  disabled={!!pdfModal.rendering || !pdfModal.rendered || !!pdfModal.error}
                  onClick={() => openPrintWindow(pdfModal.rendered)}
                >
                  {pdfModal.rendering ? 'Preparing…' : 'Print'}
                </button>
                <button
                  className="pdf-modal-download-btn"
                  disabled={!!pdfModal.rendering || !pdfModal.rendered || !!pdfModal.error || folioPdfBusy}
                  onClick={() => downloadRenderedFolio(pdfModal.rendered, pdfModal.row)}
                >
                  {/* The label says what is happening: the PDF is rendered on a server and a cold
                      Chromium takes a second or two, so a button that looked idle would be clicked twice. */}
                  {pdfModal.rendering ? 'Preparing…' : folioPdfBusy ? 'Creating PDF…' : 'Download'}
                </button>
              </div>
            )}
            {pdfModal.hasDocument !== false && (
            <div className="pdf-modal-footer">
              <button
                className="pdf-modal-print-btn"
                disabled={!!pdfModal.actionLoading || !!pdfModal.loading || !!pdfModal.error}
                onClick={() => handleFolioPdfAction('print', pdfModal.s3key)}
              >
                {pdfModal.actionLoading === 'print' ? 'Opening…' : 'Print'}
              </button>
              <button
                className="pdf-modal-download-btn"
                disabled={!!pdfModal.actionLoading || !!pdfModal.loading || !!pdfModal.error}
                onClick={() => handleFolioPdfAction('download', pdfModal.s3key)}
              >
                {pdfModal.actionLoading === 'download' ? 'Downloading…' : 'Download'}
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="payload-modal-overlay" onClick={() => setEmailModal(null)}>
          <div className="email-modal" onClick={e => e.stopPropagation()}>
            <div className="payload-modal-header">
              <span className="payload-modal-title">Send Email</span>
              <button className="payload-modal-close" onClick={() => setEmailModal(null)}>✕ Close</button>
            </div>
            <div className="email-modal-body">
              {emailModal.sent ? (
                <div className="email-modal-success">Email sent successfully.</div>
              ) : (
                <>
                  <div className="email-modal-field">
                    <label>To</label>
                    <input
                      type="email"
                      value={emailModal.to}
                      onChange={e => setEmailModal(prev => ({ ...prev, to: e.target.value }))}
                      placeholder="recipient@example.com"
                    />
                  </div>
                  <div className="email-modal-field">
                    <label>Subject</label>
                    <input
                      type="text"
                      value={emailModal.subject}
                      onChange={e => setEmailModal(prev => ({ ...prev, subject: e.target.value }))}
                    />
                  </div>
                  {emailModal.error && (
                    <div className="email-modal-error">{emailModal.error}</div>
                  )}
                  <div className="email-modal-actions">
                    <button
                      className="email-modal-send-btn"
                      onClick={handleSendEmail}
                      disabled={emailModal.sending || !emailModal.to || !emailModal.subject}
                    >
                      {emailModal.sending ? 'Sending...' : 'Send'}
                    </button>
                    <button className="email-modal-cancel-btn" onClick={() => setEmailModal(null)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {confirmModal && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-body">
              <p>{confirmModal.message}</p>
            </div>
            <div className="confirm-modal-actions">
              <button className="btn btn-secondary" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { setConfirmModal(null); confirmModal.onConfirm(); }}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VaultForm;
